/**
 * 翻译key常量定义
 *
 * 这个文件同时提供：
 * 1. 运行时常量（防止拼写错误）
 * 2. IDE 自动补全支持
 * 3. 类型推断（通过 JSDoc）
 *
 * 使用方式：
 * import { I18N_KEYS } from '@/i18n/keys'
 * $st(I18N_KEYS.COMMON.SUBMIT) // 自动补全 + 常量保护
 *
 * @module i18n/keys
 */

 /**
 * 翻译key常量对象
 * 采用嵌套结构，与翻译文件的结构保持一致
 */
export const I18N_KEYS = {
  // ============================================
  // 通用模块 (Common)
  // ============================================
  COMMON: {
    LANGUAGE: 'common.language',
    FILE_SIZE_LIMIT_NAMED: 'common.file_size_limit_named',
    FILE_SIZE_LIMIT: 'common.file_size_limit',
    LANGUAGE_SWITCH_SUCCESS: 'common.language_switch_success',
    LANGUAGE_SWITCH_FAILED: 'common.language_switch_failed',
    BACK_TOP: 'common.back_top',
    CONTACT_NOW: 'common.contact_now',
    SUBMIT: 'common.submit',
    RESET: 'common.reset',
    CANCEL: 'common.cancel',
    CONFIRM: 'common.confirm',
    YES: 'common.yes',
    EM_DASH: 'common.em_dash',
    DELETE: 'common.delete',
    DOWNLOAD: 'common.download',
    CLICK_DOWNLOAD: 'common.click_download',
    CLICK_UPLOAD: 'common.click_upload',
    DESIGN: 'common.design',
    STATUS: 'common.status',
    DESIGNED: 'common.designed',
    NOT_DESIGNED: 'common.not_designed',
    TO_BE_DESIGNED: 'common.to_be_designed',
    PROMPT: 'common.prompt',
    CONFIRM_PERMANENTLY_DELETE_PROJECT_CONTINUE: 'common.confirm_permanently_delete_project_continue',
    DELETE_SUCCESS: 'common.delete_success',
    DELETE_FAILED: 'common.delete_failed',
    DELETE_CANCELED: 'common.delete_canceled',
    ADD_SUCCESS: 'common.add_success',
    SERIAL_NUMBER: 'common.serial_number',
    NAME: 'common.name',
    OPERATIONS: 'common.operations',
    REQUIRED: 'common.required',
    OPTIONAL: 'common.optional',
    BACK_TO_LIST: 'common.back_to_list',
    EDIT: 'common.edit',
    UPLOADED: 'common.uploaded',
    NOT_UPLOADED: 'common.not_uploaded',
    IMAGE_NOT_DESIGNED_OR_FAILED: 'common.image_not_designed_or_failed',
    PLEASE_SELECT: 'common.please_select',
    PLEASE_SELECT_IMAGE: 'common.please_select_image',
    PLEASE_SELECT_GDT_FILE: 'common.please_select_gdt_file',
    PLEASE_SELECT_DAYS_TO_PURCHASE: 'common.please_select_days_to_purchase',
    CLOSE: 'common.close',
    CREATE: 'common.create',
    CLICK_TO_UPLOAD: 'common.click_to_upload',
    NO_SELECTION: 'common.no_selection',
    CONFIRM_CLOSE: 'common.confirm_close',
    PLEASE_SELECT_FILE: 'common.please_select_file',
    PROCESS_JSON_ERROR: 'common.process_json_error',
    NO_DATA: 'common.no_data',
    NO_RELATED_PROJECTS: 'common.no_related_projects',
    NO_RELATED_PROJECT_IDS: 'common.no_related_project_ids',
    DOWNLOAD_FAILED: 'common.download_failed',
    VIDEO_BADGE: 'common.video_badge',
  },

  // ============================================
  // 钱包与订阅 (Wallet)
  // ============================================
  WALLET: {
    BACK_TO_HOME: 'wallet.back_to_home',
    AVAILABLE_BALANCE: 'wallet.available_balance',
    MONTHLY_POINTS_LABEL: 'wallet.monthly_points_label',
    PERMANENT_POINTS_LABEL: 'wallet.permanent_points_label',
    COIN_UNIT: 'wallet.coin_unit',
    REFRESH_BALANCE: 'wallet.refresh_balance',
    GO_RECHARGE: 'wallet.go_recharge',
    PLAN_MONTHLY_BADGE: 'wallet.plan_monthly_badge',
    COIN_PLAN_TITLE: 'wallet.coin_plan_title',
    COIN_PLAN_SUBTITLE: 'wallet.coin_plan_subtitle',
    TOTAL_COINS_LABEL: 'wallet.total_coins_label',
    CURRENT_COINS_LABEL: 'wallet.current_coins_label',
    USED_COINS_LABEL: 'wallet.used_coins_label',
    PLAN_PERIOD_LABEL: 'wallet.plan_period_label',
    START_AT_LABEL: 'wallet.start_at_label',
    END_AT_LABEL: 'wallet.end_at_label',
    PLAN_DETAIL_BUTTON: 'wallet.plan_detail_button',
    RECENT_TRANSACTIONS_TITLE: 'wallet.recent_transactions_title',
    RECENT_TRANSACTIONS_DESC: 'wallet.recent_transactions_desc',
    REFRESH: 'wallet.refresh',
    VIEW_ALL: 'wallet.view_all',
    LOADING_TRANSACTIONS: 'wallet.loading_transactions',
    NO_RECENT_TRANSACTIONS: 'wallet.no_recent_transactions',
    TRANSACTION_TAB: 'wallet.transaction_tab',
    ORDERS_TAB: 'wallet.orders_tab',
    FILTER_TYPE_PLACEHOLDER: 'wallet.filter_type_placeholder',
    RANGE_SEPARATOR: 'wallet.range_separator',
    START_TIME_PLACEHOLDER: 'wallet.start_time_placeholder',
    END_TIME_PLACEHOLDER: 'wallet.end_time_placeholder',
    REF_ID_PLACEHOLDER: 'wallet.ref_id_placeholder',
    FILTER_APPLY: 'wallet.filter_apply',
    FILTER_RESET: 'wallet.filter_reset',
    FILTER_EXPORT: 'wallet.filter_export',
    NO_TRANSACTIONS: 'wallet.no_transactions',
    BALANCE_AFTER_PREFIX: 'wallet.balance_after_prefix',
    PREV_PAGE: 'wallet.prev_page',
    PAGE_INDICATOR: 'wallet.page_indicator',
    NEXT_PAGE: 'wallet.next_page',
    ORDER_STATUS_PLACEHOLDER: 'wallet.order_status_placeholder',
    LOADING_ORDERS: 'wallet.loading_orders',
    NO_ORDERS: 'wallet.no_orders',
    ORDER_ID_PREFIX: 'wallet.order_id_prefix',
    PAYMENT_AMOUNT_PREFIX: 'wallet.payment_amount_prefix',
    VIEW_DETAIL: 'wallet.view_detail',
    MY_FAVORITE: 'wallet.my_favorite',
    NEW_USER_NOTICE: 'wallet.new_user_notice',
    LOADING_FAVORITES: 'wallet.loading_favorites',
    NO_FAVORITES: 'wallet.no_favorites',
    FETCH_FAVORITES_FAILED: 'wallet.fetch_favorites_failed',
    FAVORITE_TARGET_MISSING: 'wallet.favorite_target_missing',
    FAVORITE_TOTAL: 'wallet.favorite_total',
    FAVORITE_SHOW_MORE: 'wallet.favorite_show_more',
    ORDER_DETAIL_TITLE: 'wallet.order_detail_title',
    DETAIL_LOADING: 'wallet.detail_loading',
    ORDER_ID_LABEL: 'wallet.order_id_label',
    STATUS_LABEL: 'wallet.status_label',
    ORDER_TYPE_LABEL: 'wallet.order_type_label',
    EXPECTED_COINS_LABEL: 'wallet.expected_coins_label',
    PAID_AMOUNT_LABEL: 'wallet.paid_amount_label',
    PAYMENT_CHANNEL_LABEL: 'wallet.payment_channel_label',
    IDEMPOTENCY_KEY_LABEL: 'wallet.idempotency_key_label',
    CREATED_AT_LABEL: 'wallet.created_at_label',
    UPDATED_AT_LABEL: 'wallet.updated_at_label',
    PAID_AT_LABEL: 'wallet.paid_at_label',
    DETAIL_EMPTY: 'wallet.detail_empty',
    APPLYING_INVOICE: 'wallet.applying_invoice',
    APPLY_INVOICE: 'wallet.apply_invoice',
    VIEW_RELATED_TRANSACTIONS: 'wallet.view_related_transactions',
    NEED_LOGIN: 'wallet.need_login',
    FETCH_WALLET_FAILED: 'wallet.fetch_wallet_failed',
    FETCH_TRANSACTIONS_FAILED: 'wallet.fetch_transactions_failed',
    FETCH_ORDERS_FAILED: 'wallet.fetch_orders_failed',
    APPLY_INVOICE_TODO: 'wallet.apply_invoice_todo',
    INVOICE_DOWNLOAD_SUCCESS: 'wallet.invoice_download_success',
    INVOICE_NOT_PAID: 'wallet.invoice_not_paid',
    INVOICE_MISSING_KEY: 'wallet.invoice_missing_key',
    INVOICE_DOWNLOAD_FAILED: 'wallet.invoice_download_failed',
    TRANSACTION_DESC_TOPUP: 'wallet.transaction_desc_topup',
    TRANSACTION_DESC_CONSUME: 'wallet.transaction_desc_consume',
    TRANSACTION_DESC_ADJUSTMENT: 'wallet.transaction_desc_adjustment',
    TRANSACTION_DESC_PROJECT_CONSUME: 'wallet.transaction_desc_project_consume',
    TRANSACTION_DESC_DESIGN_FAILED_REFUND: 'wallet.transaction_desc_design_failed_refund',
    TRANSACTION_DESC_MONTHLY_POINTS_ISSUED: 'wallet.transaction_desc_monthly_points_issued',
    TRANSACTION_DESC_EXPIRED_POINTS_DEDUCTED: 'wallet.transaction_desc_expired_points_deducted',
    TRANSACTION_DESC_ADMIN_DEDUCT_MONTHLY: 'wallet.transaction_desc_admin_deduct_monthly',
    TRANSACTION_DESC_ADMIN_ISSUE_MONTHLY: 'wallet.transaction_desc_admin_issue_monthly',
    TRANSACTION_DESC_ADMIN_DEDUCT_PERMANENT: 'wallet.transaction_desc_admin_deduct_permanent',
    TRANSACTION_DESC_ADMIN_ISSUE_PERMANENT: 'wallet.transaction_desc_admin_issue_permanent',
    TRANSACTION_DESC_REGISTER_BONUS: 'wallet.transaction_desc_register_bonus',
    TRANSACTION_DESC_CAMPAIGN_CODE: 'wallet.transaction_desc_campaign_code',
    TRANSACTION_TYPE_ALL: 'wallet.transaction_type_all',
    TRANSACTION_TYPE_TOPUP: 'wallet.transaction_type_topup',
    TRANSACTION_TYPE_CONSUME: 'wallet.transaction_type_consume',
    TRANSACTION_TYPE_ADJUST: 'wallet.transaction_type_adjust',
    ORDER_STATUS_ALL: 'wallet.order_status_all',
    ORDER_STATUS_INIT: 'wallet.order_status_init',
    ORDER_STATUS_PROCESSING: 'wallet.order_status_processing',
    ORDER_STATUS_SUCCESS: 'wallet.order_status_success',
    ORDER_STATUS_FAILED: 'wallet.order_status_failed',
    ORDER_STATUS_CANCELLED: 'wallet.order_status_cancelled',
    SHORTCUT_7_DAYS: 'wallet.shortcut_7_days',
    SHORTCUT_30_DAYS: 'wallet.shortcut_30_days',
    SHORTCUT_90_DAYS: 'wallet.shortcut_90_days',
    EXPORT_BILL_TODO: 'wallet.export_bill_todo',
    FETCH_ORDER_DETAIL_FAILED: 'wallet.fetch_order_detail_failed',
    ORDER_TYPE_TOPUP: 'wallet.order_type_topup',
    ORDER_TYPE_CONSUME: 'wallet.order_type_consume',
    ORDER_TYPE_REFUND: 'wallet.order_type_refund',
    ORDER_DESC_TOPUP: 'wallet.order_desc_topup',
    ORDER_DESC_CONSUME: 'wallet.order_desc_consume',
    ORDER_DESC_MONTHLY_STANDARD: 'wallet.order_desc_monthly_standard',
    ORDER_DESC_SUBSCRIPTION_RENEWAL: 'wallet.order_desc_subscription_renewal',
    ORDER_DESC_REFUND: 'wallet.order_desc_refund',
    USAGE_QUOTA_TITLE: 'wallet.usage_quota_title',
    USAGE_QUOTA_LOADING: 'wallet.usage_quota_loading',
    USAGE_QUOTA_IMAGE_LABEL: 'wallet.usage_quota_image_label',
    USAGE_QUOTA_VIDEO_LABEL: 'wallet.usage_quota_video_label',
    USAGE_QUOTA_UPSCALE_LABEL: 'wallet.usage_quota_upscale_label',
    USAGE_QUOTA_UNLIMITED: 'wallet.usage_quota_unlimited',
    USAGE_QUOTA_FOOTNOTE: 'wallet.usage_quota_footnote',
    REGISTRATION_TRIAL_EXPIRY_LABEL: 'wallet.registration_trial_expiry_label',

    // Subscription (Monthly plan)
    SUBSCRIPTION_STATUS_LABEL: 'wallet.subscription_status_label',
    SUBSCRIPTION_STATUS_ACTIVE: 'wallet.subscription_status_active',
    SUBSCRIPTION_STATUS_INACTIVE: 'wallet.subscription_status_inactive',
    SUBSCRIPTION_STATUS_CANCELLED: 'wallet.subscription_status_cancelled',
    SUBSCRIPTION_STATUS_ACTIVE_CANCELLED: 'wallet.subscription_status_active_cancelled',
    SUBSCRIPTION_SERVICE_TITLE: 'wallet.subscription_service_title',
    SUBSCRIPTION_BUTTON_SUBSCRIBE: 'wallet.subscription_button_subscribe',
    SUBSCRIPTION_BUTTON_UNSUBSCRIBE: 'wallet.subscription_button_unsubscribe',
    SUBSCRIPTION_BUTTON_PROCESSING: 'wallet.subscription_button_processing',
    SUBSCRIPTION_END_TIME_LABEL: 'wallet.subscription_end_time_label',
    SUBSCRIPTION_NEXT_BILLING_LABEL: 'wallet.subscription_next_billing_label',
    SUBSCRIPTION_CONFIRM_SUBSCRIBE_MESSAGE: 'wallet.subscription_confirm_subscribe_message',
    SUBSCRIPTION_CONFIRM_UNSUBSCRIBE_MESSAGE1: 'wallet.subscription_confirm_unsubscribe_message1',
    SUBSCRIPTION_CONFIRM_UNSUBSCRIBE_MESSAGE2: 'wallet.subscription_confirm_unsubscribe_message2',
    SUBSCRIPTION_CONFIRM_TITLE: 'wallet.subscription_confirm_title',
    SUBSCRIPTION_REDIRECTING_TO_PAY: 'wallet.subscription_redirecting_to_pay',
    SUBSCRIPTION_REDIRECTING_TO_PROCESS: 'wallet.subscription_redirecting_to_process',
    SUBSCRIPTION_SUBSCRIBE_SUCCESS: 'wallet.subscription_subscribe_success',
    SUBSCRIPTION_UNSUBSCRIBE_REQUEST_SUBMITTED: 'wallet.subscription_unsubscribe_request_submitted',
    SUBSCRIPTION_SUBSCRIBE_FAILED: 'wallet.subscription_subscribe_failed',
    SUBSCRIPTION_UNSUBSCRIBE_FAILED: 'wallet.subscription_unsubscribe_failed',
    SUBSCRIPTION_PAYMENT_CANCELLED: 'wallet.subscription_payment_cancelled',
    SUBSCRIPTION_PAYMENT_FAILED: 'wallet.subscription_payment_failed',
    SUBSCRIPTION_CANNOT_CANCEL_DURING_FREE_TRIAL: 'wallet.subscription_cannot_cancel_during_free_trial',
    SUBSCRIPTION_DESCRIPTION_TITLE: 'wallet.subscription_description_title',
    SUBSCRIPTION_DESCRIPTION_STEP_1: 'wallet.subscription_description_step_1',
    SUBSCRIPTION_DESCRIPTION_STEP_2: 'wallet.subscription_description_step_2',
    SUBSCRIPTION_DESCRIPTION_STEP_3: 'wallet.subscription_description_step_3',
    SUBSCRIPTION_DESCRIPTION_STEP_4: 'wallet.subscription_description_step_4',
    SUBSCRIPTION_DESCRIPTION_STEP_5: 'wallet.subscription_description_step_5',
    SUBSCRIPTION_DESCRIPTION_STEP_6: 'wallet.subscription_description_step_6',
    SUBSCRIPTION_DESCRIPTION_STEP_7: 'wallet.subscription_description_step_7',
    SUBSCRIPTION_DESCRIPTION_STEP_8: 'wallet.subscription_description_step_8',
    SUBSCRIPTION_DESCRIPTION_STEP_9: 'wallet.subscription_description_step_9',
    SUBSCRIPTION_GMO_DESCRIPTION_STEP_2: 'wallet.subscription_gmo_description_step_2',
    SUBSCRIPTION_GMO_DESCRIPTION_STEP_3: 'wallet.subscription_gmo_description_step_3',
    SUBSCRIPTION_GMO_DESCRIPTION_STEP_4: 'wallet.subscription_gmo_description_step_4',
    SUBSCRIPTION_GMO_DESCRIPTION_STEP_5: 'wallet.subscription_gmo_description_step_5',
    SUBSCRIPTION_DESCRIPTION_FOOTNOTE: 'wallet.subscription_description_footnote',
    // 订阅计划详情（弹窗左侧）
    SUBSCRIPTION_PLAN_TITLE: 'wallet.subscription_plan_title',
    SUBSCRIPTION_PLAN_FREE_MONTH: 'wallet.subscription_plan_free_month',
    SUBSCRIPTION_PLAN_MONTHLY_FEE: 'wallet.subscription_plan_monthly_fee',
    SUBSCRIPTION_PLAN_CANCEL_ANYTIME: 'wallet.subscription_plan_cancel_anytime',
    SUBSCRIPTION_PLAN_MONTHLY_POINTS: 'wallet.subscription_plan_monthly_points',
    SUBSCRIPTION_PLAN_MONTHLY_QUOTA_BENEFIT: 'wallet.subscription_plan_monthly_quota_benefit',
    SUBSCRIPTION_PLAN_MONTHLY_QUOTA_BENEFIT_NOTE: 'wallet.subscription_plan_monthly_quota_benefit_note',
    SUBSCRIPTION_PLAN_FREE_MONTH_NOTE: 'wallet.subscription_plan_free_month_note',
    SUBSCRIPTION_PLAN_CANCEL_NOTE: 'wallet.subscription_plan_cancel_note',
    SUBSCRIPTION_GMO_PLAN_FREE_MONTH: 'wallet.subscription_gmo_plan_free_month',
    SUBSCRIPTION_GMO_PLAN_FREE_MONTH_NOTE: 'wallet.subscription_gmo_plan_free_month_note',
    SUBSCRIPTION_GMO_PLAN_CANCEL_NOTE: 'wallet.subscription_gmo_plan_cancel_note',
    SUBSCRIPTION_PLAN_TERMS_NOTE: 'wallet.subscription_plan_terms_note',
    SUBSCRIPTION_PLAN_APPLY_BUTTON: 'wallet.subscription_plan_apply_button',
    SUBSCRIPTION_CAMPAIGN_CODE_PLACEHOLDER: 'wallet.subscription_campaign_code_placeholder',
    SUBSCRIPTION_CAMPAIGN_CODE_INVALID: 'wallet.subscription_campaign_code_invalid',
    SUBSCRIPTION_CAMPAIGN_ORIGINAL_AMOUNT: 'wallet.subscription_campaign_original_amount',
    SUBSCRIPTION_CAMPAIGN_DISCOUNT_AMOUNT: 'wallet.subscription_campaign_discount_amount',
    SUBSCRIPTION_CAMPAIGN_FINAL_AMOUNT: 'wallet.subscription_campaign_final_amount',
    SUBSCRIPTION_TRY_FIRST_BUTTON: 'wallet.subscription_try_first_button',
    SUBSCRIPTION_GMO_TRY_FIRST_BUTTON: 'wallet.subscription_gmo_try_first_button',
    SUBSCRIPTION_TRY_FIRST_DESC: 'wallet.subscription_try_first_desc',
  },

  // ============================================
  // 钱包充值 (Wallet Recharge)
  // ============================================
  RECHARGE: {
    BACK_TO_WALLET: 'recharge.back_to_wallet',
    SELECT_PACKAGE_TITLE: 'recharge.select_package_title',
    FLOW_TITLE: 'recharge.flow_title',
    FLOW_STEP_1: 'recharge.flow_step_1',
    FLOW_STEP_2: 'recharge.flow_step_2',
    FLOW_STEP_3: 'recharge.flow_step_3',
    FLOW_STEP_4: 'recharge.flow_step_4',
    LOADING_PACKAGES: 'recharge.loading_packages',
    EMPTY_PACKAGES: 'recharge.empty_packages',
    PACKAGE_EXPECTED_COINS: 'recharge.package_expected_coins',
    PACKAGE_BASE_COINS: 'recharge.package_base_coins',
    PACKAGE_BONUS_COINS: 'recharge.package_bonus_coins',
    PACKAGE_NO_BONUS: 'recharge.package_no_bonus',
    BUTTON_CREATING: 'recharge.button_creating',
    BUTTON_PURCHASE: 'recharge.button_purchase',
    ORDER_PAYMENT_TITLE: 'recharge.order_payment_title',
    SCAN_HINT: 'recharge.scan_hint',
    PAYMENT_SUPPORT: 'recharge.payment_support',
    ORDER_ID_LABEL: 'recharge.order_id_label',
    MERCHANT_ORDER_ID_LABEL: 'recharge.merchant_order_id_label',
    PACKAGE_EXPECTED_COINS_LABEL: 'recharge.package_expected_coins_label',
    CONTINUE_SELECT: 'recharge.continue_select',
    VIEW_WALLET: 'recharge.view_wallet',
    CURRENCY_CNY: 'recharge.currency_cny',
    CURRENCY_JPY: 'recharge.currency_jpy',
    CURRENCY_USD: 'recharge.currency_usd',
    LOAD_PACKAGES_FAILED: 'recharge.load_packages_failed',
    LOGIN_REQUIRED: 'recharge.login_required',
    CREATE_ORDER_FAILED: 'recharge.create_order_failed',
    CREATE_ORDER_RETRY: 'recharge.create_order_retry',
    ORDER_STATUS_TIMEOUT: 'recharge.order_status_timeout',
    PAYMENT_SUCCESS: 'recharge.payment_success',
    PAYMENT_PENDING: 'recharge.payment_pending',
    STATUS_AWAITING_PAYMENT: 'recharge.status_awaiting_payment',
    STATUS_MESSAGE_DEFAULT: 'recharge.status_message_default',
    STATUS_MESSAGE_SUCCESS: 'recharge.status_message_success',
    STATUS_MESSAGE_PENDING: 'recharge.status_message_pending',
    STATUS_MESSAGE_PROCESSING: 'recharge.status_message_processing',
    MONTHLY_PLAN_BADGE: 'recharge.monthly_plan_badge',
    PER_MONTH_SUFFIX: 'recharge.per_month_suffix',
    IMAGE_UNIT: 'recharge.image_unit',
    PACKAGE_IMAGES_INCLUDED: 'recharge.package_images_included',
    PACKAGE_EXPIRATION_DAYS: 'recharge.package_expiration_days',
    PACKAGE_MONTHLY_DESC: 'recharge.package_monthly_desc',
    // 充值套餐信息卡片（弹窗左侧）
    RECHARGE_PACKAGE_INFO_NO_EXPIRATION: 'recharge.package_info_no_expiration',
    RECHARGE_PACKAGE_INFO_TERMS: 'recharge.package_info_terms',
    RECHARGE_PACKAGE_INFO_BUTTON: 'recharge.package_info_button',
    PACKAGE_NAME_1: 'recharge.package_name_1',
    PACKAGE_NAME_2: 'recharge.package_name_2',
    PACKAGE_NAME_3: 'recharge.package_name_3',
    PACKAGE_NAME_4: 'recharge.package_name_4',
    PACKAGE_NAME_5: 'recharge.package_name_5',
    PACKAGE_NAME_6: 'recharge.package_name_6',
    PACKAGE_NAME_7: 'recharge.package_name_7',
    PACKAGE_NAME_8: 'recharge.package_name_8',
    PACKAGE_NAME_9: 'recharge.package_name_9',
    PACKAGE_NAME_10: 'recharge.package_name_10',
    // 商家信息表格
    MERCHANT_INFO_TITLE: 'recharge.merchant_info_title',
    MERCHANT_NAME: 'recharge.merchant_name',
    MERCHANT_NAME_VALUE: 'recharge.merchant_name_value',
    REPRESENTATIVE: 'recharge.representative',
    REPRESENTATIVE_VALUE: 'recharge.representative_value',
    ADDRESS: 'recharge.address',
    ADDRESS_VALUE: 'recharge.address_value',
    PHONE: 'recharge.phone',
    PHONE_VALUE: 'recharge.phone_value',
    PHONE_HOURS: 'recharge.phone_hours',
    PHONE_HOURS_VALUE: 'recharge.phone_hours_value',
    EMAIL: 'recharge.email',
    EMAIL_VALUE: 'recharge.email_value',
    SITE_URL: 'recharge.site_url',
    SITE_URL_VALUE: 'recharge.site_url_value',
    SELLING_PRICE: 'recharge.selling_price',
    SELLING_PRICE_VALUE: 'recharge.selling_price_value',
    ADDITIONAL_FEES: 'recharge.additional_fees',
    ADDITIONAL_FEES_VALUE: 'recharge.additional_fees_value',
    DELIVERY_TIME: 'recharge.delivery_time',
    DELIVERY_TIME_VALUE: 'recharge.delivery_time_value',
    PAYMENT_METHOD: 'recharge.payment_method',
    PAYMENT_METHOD_VALUE: 'recharge.payment_method_value',
    RETURN_POLICY: 'recharge.return_policy',
    RETURN_POLICY_VALUE: 'recharge.return_policy_value',
    RETURN_DEADLINE: 'recharge.return_deadline',
    RETURN_DEADLINE_VALUE: 'recharge.return_deadline_value',
    RETURN_SHIPPING: 'recharge.return_shipping',
    RETURN_SHIPPING_VALUE: 'recharge.return_shipping_value',
    ANTIQUE_LICENSE: 'recharge.antique_license',
    ANTIQUE_LICENSE_VALUE: 'recharge.antique_license_value',
  },

  // ============================================
  // 导航和页面标题 (Navigation)
  // ============================================
  NAV: {
    INITIAL_PAGE: 'nav.initial_page',
    MEMBER_SITE: 'nav.member_site',
    MEMBER_INQUIRY: 'nav.member_inquiry',
    INTEL_PRODUCT: 'nav.intel_product',
    DEVELOPING_PRODUCTS: 'nav.developing_products',
    TYPICAL_CASES: 'nav.typical_cases',
    HEMU_NEWS: 'nav.HemuNews',
    ABOUT_HEMU: 'nav.about_hemu',
    ABOUT_US: 'nav.about_us',
    SIGN_UP: 'nav.sign_up',
    MAIN_BUSINESS: 'nav.main_business',
    WALLET: 'nav.wallet',
    FACADE: 'nav.facade',
    INTERIOR: 'nav.interior',
    DRAWING_VECTORIZE: 'nav.drawing_vectorize',
  },

  // ============================================
  // 产品相关 (Products)
  // ============================================
  PRODUCTS: {
    SHEARWALL_STRUCTURE: 'products.shearwall_structure',
    FRAME_STRUCTURE: 'products.frame_structure',
    CORE_STRUCTURE: 'products.core_structure',
    STRUCTURE_INTEL: 'products.structure_intel',
    WATCH_VIDEO: 'products.watch_video',
    PRODUCTION_AND_SOLUTION: 'products.production_and_solution',
    LOCAL_ASSISTANT: 'products.local_assistant',
    LOCAL_ASSISTANT_CONTENT: 'products.local_assistant_content',
    PLATFORM_CLOUD: 'products.platform_cloud',
    PLATFORM_CLOUD_CONTENT: 'products.platform_cloud_content',
    SHEARWALL_STRUCTURE_DESIGN_TITLE: 'products.shearwall_structure_design_title',
    SHEARWALL_STRUCTURE_DESIGN_CONTENT: 'products.shearwall_structure_design_content',
    FRAME_STRUCTURE_DESIGN_TITLE: 'products.frame_structure_design_title',
    LOOK_FORWARD: 'products.look_forward',
    CORE_STRUCTURE_DESIGN_TITLE: 'products.core_structure_design_title',
    CORE_STRUCTURE_DESGIN_CONTENT: 'products.core_structure_desgin_content',
    KEY_COMPONENT: 'products.key_component',
    KEY_COMPONENT_CONTENT1: 'products.key_component_content1',
    KEY_COMPONENT_CONTENT2: 'products.key_component_content2',
    PATENT: 'products.patent',
    SOFTWARE_COPYRIGHTS: 'products.software_copyrights',
    ENTER_CLOUD: 'products.enter_cloud',
    PRODUCT_DOWNLOAD: 'products.product_download',
    AISTRUCTURE_COPILOT: 'products.AIstructure-Copilot',
    AISTRUCTURE_COPILOT_TIPS: 'products.AIstructure-Copilot_tips',
    INTEL_PRODUCT_CONTENT1: 'products.intel_product_content1',
    INTEL_PRODUCT_CONTENT2: 'products.intel_product_content2',
    INTEL_PLATFORM: 'products.intel_platform',
    INTEL_PLATFORM_TIPS: 'products.intel_platform_tips',
    SIGN_IN: 'products.sign_in',
    INSTRUCTION: 'products.instruction',
    INTEL_PLATFORM_CONTENT1: 'products.intel_platform_content1',
    INTEL_PLATFORM_CONTENT2: 'products.intel_platform_content2',
    AUTOMATED_RULE_INTERPRETATION: 'products.automated_rule_interpretation',
    AUTOMATED_RULE_INTERPRETATION_CONTENT1: 'products.automated_rule_interpretation_content1',
    AUTOMATED_RULE_INTERPRETATION_CONTENT2: 'products.automated_rule_interpretation_content2',
    ENTER_AUTOMATED: 'products.enter_automated',
    // 兼容旧常量，后续可删除
    CLOUD_PLATFORM: 'products.platform_cloud',
    CLOUD_PLATFORM_CONTENT: 'products.platform_cloud_content',
    COPILOT_TIPS: 'products.AIstructure-Copilot_tips',
  },

  // ============================================
  // 认证相关 (Authentication)
  // ============================================
  AUTH: {
    // 基础认证
    HELLO: 'auth.hello',
    PLEASE_SIGN_IN: 'auth.please_sign_in',
    PLEASE_SIGN_UP: 'auth.please_sign_up',
    CLICK_TO_SIGN_UP: 'auth.click_to_sign_up',
    SIGN_IN_NOW: 'auth.sign_in_now',
    SIGN_UP_NOW: 'auth.sign_up_now',
    ACCOUNT_SIGN_IN_NOW: 'auth.account_sign_in_now',

    // 登录相关
    PASSWORD_LOGIN: 'auth.password_login',
    PHONE_CODE_LOGIN: 'auth.phone_code_login',
    EMAIL_CODE_LOGIN: 'auth.email_code_login',
    LOGIN_NUMBER: 'auth.login_number',
    SIGN_IN_BY_PASSWORD: 'auth.sign_in_by_password',
    SIGN_IN_BY_CODE: 'auth.sign_in_by_code',
    PASSWORD: 'auth.password',
    PHONE_NUMBER: 'auth.phone_number',
    EMAIL: 'auth.email',
    LOGIN_PHONE: 'auth.login_phone',
    LOGIN_EMAIL: 'auth.login_email',
    CODE: 'auth.code',
    GET_CODE: 'auth.get_code',
    CODE_SENT_SUCCESS: 'auth.code_sent_success',
    EMAIL_CODE_SENT_TO: 'auth.email_code_sent_to',
    ENTER_CODE_FROM_EMAIL: 'auth.enter_code_from_email',
    FORGET_PASSWORD: 'auth.forget_password',

    // 注册相关
    SIGN_IN_BY_PHONE: 'auth.sign_in_by_phone',
    SIGN_IN_BY_EMAIL: 'auth.sign_in_by_email',
    ACCOUNT_NAME: 'auth.account_name',
    CHINESE_NAME: 'auth.chinese_name',
    UNIT_NAME: 'auth.unit_name',
    CONFIRM_PASSWORD: 'auth.confirm_password',
    PHONE_CODE: 'auth.phone_code',
    EMAIL_CODE: 'auth.email_code',
    SEX: 'auth.sex',
    MALE: 'auth.male',
    FEMALE: 'auth.female',
    CHOOSE_SEX: 'auth.choose_sex',

    // 注册第一步标题
    CREATE_ACCOUNT: 'auth.create_account',

    // 重置密码
    RESET_PASSWORD: 'auth.reset_password',
    RESET_PHONE_PASSWORD: 'auth.reset_phone_password',
    RESET_EMAIL_PASSWORD: 'auth.reset_email_password',
    CHANGE_NOW: 'auth.change_now',
    BACK_TO_SIGN_IN: 'auth.back_to_sign_in',
    PREVIOUS_STEP: 'auth.previous_step',
    REGISTER_DIFFERENT_EMAIL: 'auth.register_different_email',
    RESEND_VERIFICATION_CODE: 'auth.resend_verification_code',
    VERIFY_CODE_NOT_RECEIVED_TITLE: 'auth.verify_code_not_received_title',
    VERIFY_CODE_NOT_RECEIVED_1: 'auth.verify_code_not_received_1',
    VERIFY_CODE_NOT_RECEIVED_2: 'auth.verify_code_not_received_2',
    VERIFY_CODE_NOT_RECEIVED_3: 'auth.verify_code_not_received_3',
    NEED_HELP_CLICK_HERE: 'auth.need_help_click_here',

    // 输入提示
    ENTER_PHONE_NUMBER: 'auth.enter_phone_number',
    ENTER_PASSWORD: 'auth.enter_password',
    ENTER_OLD_PASSWORD: 'auth.enter_old_password',
    ENTER_NEW_PASSWORD: 'auth.enter_new_password',
    ENTER_PASSWORD_AGAIN: 'auth.enter_password_again',
    ENTER_CHINESE_NAME: 'auth.enter_chinese_name',
    ENTER_ENGLISH_ACCOUNT_NAME: 'auth.enter_english_account_name',
    ENTER_UNIT_NAME: 'auth.enter_unit_name',
    ADDRESS: 'auth.address',
    ENTER_ADDRESS: 'auth.enter_address',
    PROFILE_COMPANY_HINT: 'auth.profile_company_hint',
    PROFILE_DIALOG_TITLE: 'auth.profile_dialog_title',
    PROFILE_DIALOG_DESC: 'auth.profile_dialog_desc',
    PHONE_DIALOG_TITLE: 'auth.phone_dialog_title',
    PHONE_DIALOG_HINT: 'auth.phone_dialog_hint',
    CAMPAIGN_CODE: 'auth.campaign_code',
    ENTER_CAMPAIGN_CODE: 'auth.enter_campaign_code',
    INVALID_CAMPAIGN_CODE: 'auth.invalid_campaign_code',
    EXPIRED_CAMPAIGN_CODE: 'auth.expired_campaign_code',
    ENTER_6_CODE: 'auth.enter_6_code',

    // 验证消息
    ENTER_CORRECT_PHONE_NUMBER: 'auth.enter_correct_phone_number',
    CORRECT_PASSWORD_FORMAT: 'auth.correct_password_format',
    CORRECT_CODE_FORMAT: 'auth.correct_code_format',
    INPUT_CORRECT_EMAIL_FORMAT: 'auth.input_correct_email_format',
    CORRECT_CHINESE_NAME_FORMAT: 'auth.correct_chinese_name_format',
    CORRECT_ENGLISH_NAME_FORMAT: 'auth.correct_english_name_format',

    // 状态消息
    SIGN_IN_SUCCESS: 'auth.sign_in_success',
    UPDATE_SUCCESS: 'auth.update_success',
    LOGIN_FAILED_TIPS: 'auth.login_failed_tips',
    LOGIN_SUCCESS_TIPS: 'auth.login_success_tips',
    UNCORRECT_CODE: 'auth.uncorrect_code',
    VERIFY_CODE_SENT_TOO_FREQUENTLY: 'auth.verify_code_sent_too_frequently',
    ALREADY_REGISTERED: 'auth.already_registered',

    // 通用授权提示
    AUTH_EXPIRED_RELOGIN: 'auth.auth_expired_relogin',
    
    // 错误码消息
    ERROR_E205: 'auth.error_e205', // 验证码有误
    ERROR_E206: 'auth.error_e206', // 用户不存在
    ERROR_E207: 'auth.error_e207', // 旧密码输入不正确
    ERROR_E208: 'auth.error_e208', // 用户、手机号或密码验证失败
    ERROR_E209: 'auth.error_e209', // 用户名已存在
    ERROR_E2010: 'auth.error_e2010', // 手机号已存在
    ERROR_E2011: 'auth.error_e2011', // 获取验证码失败
    ERROR_E2012: 'auth.error_e2012', // 邮箱已存在
    ERROR_E2013: 'auth.error_e2013', // 验证码发送过于频繁
    ERROR_E200: 'auth.error_e200', // token校验失败
    ERROR_E201: 'auth.error_e201', // 数据上传失败
    ERROR_E202: 'auth.error_e202', // 设计请求发送失败
    ERROR_E203: 'auth.error_e203', // 管理员token校验失败
    ERROR_E204: 'auth.error_e204', // 密码修改失败
    ERROR_E215: 'auth.error_e215', // 当前角色无权访问该接口
    ERROR_E210: 'auth.error_e210', // 设计请求超时
    ERROR_E211: 'auth.error_e211', // 数据下载失败
    ERROR_E400: 'auth.error_e400', // 数据库记录输入数据错误
    ERROR_E410: 'auth.error_e410', // 数据库记录输出数据错误
    ERROR_E411: 'auth.error_e411', // 数据库超时
    // 订单相关错误 (E3xx)
    ERROR_E300: 'auth.error_e300', // 创建订单失败
    ERROR_E301: 'auth.error_e301', // 订单状态获取失败
    ERROR_E302: 'auth.error_e302', // 订单列表查询失败
    ERROR_E303: 'auth.error_e303', // 支付详情获取失败
    ERROR_E304: 'auth.error_e304', // 用户不存在，创建订单失败
    ERROR_E305: 'auth.error_e305', // 钱包已被封禁，无法创建订单
    ERROR_E306: 'auth.error_e306', // 钱包余额不足，请充值
    ERROR_E307: 'auth.error_e307', // 支付执行失败，已支付的订单不能再次选择
    ERROR_E308: 'auth.error_e308', // 继续支付请求失败
    ERROR_E309: 'auth.error_e309', // 删除失败，只能在支付发起2小时后删除
    ERROR_E310: 'auth.error_e310', // 订阅计划ID不能为空
    ERROR_E311: 'auth.error_e311', // 不支持的订阅计划
    ERROR_E312: 'auth.error_e312', // 幂等键不能为空
    ERROR_E313: 'auth.error_e313', // 请勿提交重复订单
    ERROR_E314: 'auth.error_e314', // 用户在免费试用期间，无法取消订阅
    ERROR_E315: 'auth.error_e315', // 只有订阅用户才能充值永久点数
    ERROR_E316: 'auth.error_e316', // 系统正在处理订单，请在2:00之后再取消订阅
    ERROR_E317: 'auth.error_e317', // 上一笔设计任务仍在进行中
    ERROR_E318: 'auth.error_e318', // AI设计次数已达上限
    ERROR_E319: 'auth.error_e319', // 未找到该幂等键对应的订单
    ERROR_E320: 'auth.error_e320', // 订单未支付成功，无法生成发票
    ERROR_E321: 'auth.error_e321', // 无权访问该订单
    ERROR_E322: 'auth.error_e322', // PDF generation failed
    // 钱包相关错误 (E4xx)
    ERROR_E401: 'auth.error_e401', // 获取钱包数据失败
    // 订阅相关错误 (E5xx)
    ERROR_E500: 'auth.error_e500', // 获取订阅信息失败
    ERROR_E501: 'auth.error_e501', // 创建订阅失败
    ERROR_E502: 'auth.error_e502', // 获取订阅计划失败
    // 交易记录相关错误 (E6xx)
    ERROR_E600: 'auth.error_e600', // 查询金币流水记录失败
    // AI设计相关错误 (E7xx)
    ERROR_E700: 'auth.error_e700', // 创建AI设计失败
    ERROR_E701: 'auth.error_e701', // 创建AI设计异常
    // 图片收藏相关错误 (E8xx)
    ERROR_E800: 'auth.error_e800', // 获取图片收藏状态失败
    ERROR_E801: 'auth.error_e801', // 设置图片收藏失败
    ERROR_E802: 'auth.error_e802', // 取消图片收藏失败
    ERROR_E803: 'auth.error_e803', // 用户未登录或用户信息获取失败
    ERROR_E804: 'auth.error_e804', // 图片URL不能为空
    ERROR_E805: 'auth.error_e805', // 项目ID不能为空
    ERROR_E806: 'auth.error_e806', // 图片记录不存在，无法设置收藏
    ERROR_E807: 'auth.error_e807', // 收藏图片数量已达上限
    ERROR_E808: 'auth.error_e808', // 无法定位对应项目结果
    // 管理员账号发行相关错误 (E10xx)
    ERROR_E1000: 'auth.error_e1000', // 单个账号创建失败
    ERROR_E1001: 'auth.error_e1001', // 批量账号发行失败
    ERROR_E1002: 'auth.error_e1002', // 无修改权限（修改用户类型）
    // 活动代码相关错误 (E9xx)
    ERROR_E900: 'auth.error_e900', // 活动代码无效
    ERROR_E901: 'auth.error_e901', // 活动代码已过期
    ERROR_E902: 'auth.error_e902', // 活动代码不适用于自动续费
    ERROR_E903: 'auth.error_e903', // 活动代码仅适用于月度订阅
    ERROR_E904: 'auth.error_e904', // 活动代码使用次数已用完
    ERROR_E905: 'auth.error_e905', // 当前支付方式不支持使用活动代码

    // 其他
    BACK_TO_MAIN: 'auth.back_to_main',
    TERMS_AGREEMENT_REQUIRED: 'auth.terms_agreement_required',
    TERMS_OF_SERVICE: 'auth.terms_of_service',
    PRIVACY_POLICY: 'auth.privacy_policy',
    AGREE_TO_TERMS: 'auth.agree_to_terms',
    TERMS_OF_SERVICE_CONTENT: 'auth.terms_of_service_content',
    PRIVACY_POLICY_CONTENT: 'auth.privacy_policy_content',
    LOGIN_TERMS_AGREEMENT: 'auth.login_terms_agreement',
    TERMS_UPDATE_TITLE: 'auth.terms_update_title',
    TERMS_UPDATE_DESC: 'auth.terms_update_desc',
    TERMS_UPDATE_CHECKBOX: 'auth.terms_update_checkbox',
    TERMS_UPDATE_ACCEPT: 'auth.terms_update_accept',
    TERMS_UPDATE_DECLINE: 'auth.terms_update_decline',
    TERMS_UPDATE_ACCEPT_FAILED: 'auth.terms_update_accept_failed',
    GOOGLE_LOGIN: 'auth.google_login',
    SYSTEM_UPDATE_TIPS: 'auth.system_update_tips',
    USERNAME: 'auth.username',
    OLD_PASSWORD: 'auth.old_password',
    NEW_PASSWORD: 'auth.new_password',
    CONFIRM_NEW_PASSWORD: 'auth.confirm_new_password',
    SAME_PASSWORD_ERROR: 'auth.same_password_error',
    PASSWORDS_NOT_MATCH: 'auth.passwords_not_match',
    // 新注册流程
    INFO_CONFIRM_TITLE: 'auth.info_confirm_title',
    INFO_CONFIRM_DESC: 'auth.info_confirm_desc',
    AGREE_AND_REGISTER: 'auth.agree_and_register',
    REGISTER: 'auth.register',
    GO_TO_CREDIT_CARD: 'auth.go_to_credit_card',
    EDIT_INFORMATION: 'auth.edit_information',
    CREDIT_CARD_TITLE: 'auth.credit_card_title',
    CREDIT_CARD_PAYMENT_TYPE: 'auth.credit_card_payment_type',
    COMPLETE_CREDIT_CARD: 'auth.complete_credit_card',

    // 订阅步骤跳过
    SKIP_SUBSCRIPTION_LOGIN: 'auth.skip_subscription_login',
    REDIRECTING_TO_PAYMENT: 'auth.redirecting_to_payment',
    REGISTRATION_COMPLETE: 'auth.registration_complete',
    FIRST_BILLING_DATE: 'auth.first_billing_date',
    GO_TO_PROJECT_LIST: 'auth.go_to_project_list',
  },

  // ============================================
  // 案件管理 (Case)
  // ============================================
  CASE: {
    CREATE_CASE: 'case.common.create_case',
    ALL_CASES: 'case.common.all_cases',
    STATISTICS: 'case.common.statistics',
    MANAGEMENT: 'case.common.management',
    PRODUCT_LIST: 'case.common.product_list',
    MATERIAL_FILES: 'case.common.material_files',
    STATUS: 'case.common.status',
    ASSIGNEE: 'case.common.assignee',
    UNASSIGNED: 'case.common.unassigned',
    ALL: 'case.common.all',
    SEARCH: 'case.common.search',
    OPERATION_FAILED: 'case.common.operation_failed',
    REQUEST_FAILED: 'case.common.request_failed',
    MISSING_CASE_KEY: 'case.common.missing_case_key',
    DOWNLOAD_FAILED: 'case.common.download_failed',
    DELETE_FAILED: 'case.common.delete_failed',
    DELETE_SUCCESS: 'case.common.delete_success',
    UPLOAD_FAILED: 'case.common.upload_failed',
    PREVIEW_FAILED: 'case.common.preview_failed',
    LIST_LOAD_FAILED: 'case.list.load_failed',
    LIST_CASE_KEY_MISSING: 'case.list.case_key_missing',
    LIST_SUBMITTER_ONLY_CREATE: 'case.list.submitter_only_create',
    ONBOARDING_SKIP: 'case.onboarding.ui.skip',
    ONBOARDING_NEXT: 'case.onboarding.ui.next',
    ONBOARDING_BACK: 'case.onboarding.ui.back',
    ONBOARDING_START: 'case.onboarding.ui.start',
    ONBOARDING_CLOSE: 'case.onboarding.ui.close',
    ONBOARDING_HELP: 'case.onboarding.ui.help',
    ERROR_E1100: 'case.errors.e1100',
    ERROR_E1101: 'case.errors.e1101',
    ERROR_E1102: 'case.errors.e1102',
    ERROR_E1103: 'case.errors.e1103',
    ERROR_E1104: 'case.errors.e1104',
    ERROR_E1105: 'case.errors.e1105',
    ERROR_E1106: 'case.errors.e1106',
    ERROR_E1107: 'case.errors.e1107',
    ERROR_E1108: 'case.errors.e1108',
    ERROR_E1109: 'case.errors.e1109',
    ERROR_E1110: 'case.errors.e1110',
    ERROR_E1111: 'case.errors.e1111',
    ERROR_E1112: 'case.errors.e1112',
    ERROR_DESIGN_ASSET_CODE_EXISTS: 'case.errors.design_asset_code_exists', // 素材ライブラリの商品コード重複
  },

  // ============================================
  // 云平台相关 (Platform)
  // ============================================
  PLATFORM: {
    INTELLI_DESIGN: 'platform.intelli_design',
    CHANGE_PASSWORD: 'platform.change_password',
    ADMIN_PAGE: 'platform.admin_page',
    LOG_OUT: 'platform.log_out',
    MY_PROJECT: 'platform.my_project',
    SHEARWALL_DESIGN: 'platform.shearwall_design',
    FRAME_DESIGN: 'platform.frame_design',
    FRAME_CORE_DESIGN: 'platform.frame_core_desgin',
    CAD_PLUGIN: 'platform.cad_plugin',
    TOOL_DOWNLOAD: 'platform.tool_download',
    ORDER_LIST: 'platform.order_list',
  },

  // ============================================
  // 项目管理 (Project)
  // ============================================
  PROJECT: {
    CREATE_PROJECT: 'project.creat_project',
    PROJECT_NAME: 'project.project_name',
    ENTER_PROJECT_NAME: 'project.enter_project_name',
    ENTER_PROJECT_NAME_TO_SEARCH: 'project.enter_project_name_to_search',
    ENTER_PROJECT_TITLE: 'project.enter_project_title',
    SHEARWALL_PROJECT: 'project.shearwall_project',
    ARCHITECTURAL_RENDERING_PROJECT: 'project.architectural_rendering_project',
    IMPORT_PROJECT: 'project.import_project',
    ADD_SHEARWALL_DESIGN: 'project.add_shearwall_design',
    ADD_SHEARWALL_TIPS: 'project.add_shearwall_tips',
    VECTOR_DATA: 'project.vector_data',
    COMPONENT_IMG: 'project.component_img',
    SPACE_IMG: 'project.space_img',
    CORRECT_PROJECT_TITLE_FORMAT: 'project.correct_project_title_format',
    CHOOSE_COMPONENT_IMG: 'project.choose_component_img',
    CHOOSE_SPACE_IMG: 'project.choose_space_img',
    CHOOSE_JSON_FILE: 'project.choose_json_file',
    INPUT_ARCHI: 'project.input_archi',
    DOWNLOAD_INPUT_IMAGE: 'project.download_input_image',
    EDIT_INPUT_IMAGE: 'project.edit_input_image',
    CLICK_DESIGN_TO_GENERATE_THIS_IMAGE: 'project.click_design_to_generate_this_image',
    DESIGN_BUTTON_TIPS: 'project.design_button_tips',
    CLICK_DESIGN_BUTTON_TO_GET_OUTPUT_DESIGN: 'project.click_design_button_to_get_output_design',
    SHEARWALL_PIC_INSTRUCT: 'project.shearwall_pic_instruct',
    LATEST_TEST_VERSION: 'project.Latest_test_version',
    MATERIAL_PREDICT: 'project.material_predict',
    ALGORITHM_A: 'project.AlgorithmA',
    ALGORITHM_B: 'project.AlgorithmB',
    ALGORITHM_C: 'project.AlgorithmC',
    DESIGN_STRUCTURE_IMAGE: 'project.design_structure_image',
    SHEARWALL: 'project.shearwall',
    BEAM: 'project.beam',
    MATERIAL_USAGE: 'project.material_usage',
    DOWNLOAD_JSON: 'project.downloadJOSN',
    ARCHI_FLOOR: 'project.archi_floor',
    DIFFUSION_FLOOR: 'project.Diffusion_floor',
    GAN_FLOOR: 'project.GAN_floor',
    GNN1_FLOOR: 'project.GNN1_floor',
    GNN2_FLOOR: 'project.GNN2_floor',
    CASE_LEGEND: 'project.case_legend',
    CREATE_PROJECT_SUCCESS: 'project.create_project_success',
    CREATE_PROJECT_FAILED: 'project.create_project_failed',
    ARCHITECTURAL_RENDERING_DESIGN: 'project.Architectural_Rendering_desgin',
    DIFFUSION_MODEL: 'project.diffusion_model',
    GAN_PIC: 'project.GAN_pic',
    GNN1_PIC: 'project.GNN1_pic',
    GNN2_PIC: 'project.GNN2_pic',
    ADD_ARCHITECTURAL_RENDERING: 'project.add_architectural_rendering',
    ADD_ARCHITECTURAL_RENDERING_PROJECT: 'project.add_architectural_rendering_project',
    LOAD_PROJECT_FAILED: 'project.load_project_failed',
    ADD_RENDERING_TIPS: 'project.add_rendering_tips',
    HOUSE_IMAGE: 'project.house_image',
    BACKGROUND_IMAGE: 'project.background_image',
    TIME_PERIOD: 'project.time_period',
    RENDER_PROMPT: 'project.render_prompt',
    CHOOSE_HOUSE_IMAGE: 'project.choose_house_image',
    CHOOSE_BACKGROUND_IMAGE: 'project.choose_background_image',
    CHOOSE_TIME_PERIOD: 'project.choose_time_period',
    ENTER_RENDER_PROMPT: 'project.enter_render_prompt',
    ONLY_IMAGE_FILE: 'project.only_image_file',
    IMAGE_SIZE_MAX_50M: 'project.image_size_max_50M',
    PROJECT_NAME_MAX_50: 'project.project_name_max_50',
    PROMPT_MIN_10: 'project.prompt_min_10',
    RENDER_SUCCESS: 'project.render_success',
    RENDER_FAILED: 'project.render_failed',
    LOAD_DETAIL_FAILED: 'project.load_detail_failed',
    // 新增：梁与剪力墙编辑、字段与校验
    EDIT_BEAM_DESIGN: 'project.edit_beam_design',
    EDIT_SHEARWALL_DESIGN: 'project.edit_shearwall_design',
    EDIT_INPUT_DESIGN: 'project.edit_input_design',
    BEAM_DESIGN_METHOD: 'project.beam_design_method',
    BOUNDARY_LENGTH: 'project.boundary_length',
    BEAM_HEIGHT_MAX: 'project.beam_height_max',
    BEAM_HEIGHT_MIN: 'project.beam_height_min',
    CONDITION_N: 'project.condition_n',
    ENTER_BEAM_DESIGN_METHOD: 'project.enter_beam_design_method',
    ENTER_BOUNDARY_LENGTH: 'project.enter_boundary_length',
    ENTER_BEAM_HEIGHT_MIN: 'project.enter_beam_height_min',
    ENTER_BEAM_HEIGHT_MAX: 'project.enter_beam_height_max',
    CHOOSE_STRUCTURE_IMAGE: 'project.choose_structure_image',
    CHOOSE_SPACE_LAYOUT_IMAGE: 'project.choose_space_layout_image',
    SHEARWALL_DESIGN_IMAGE: 'project.shearwall_design_image',
    SHEARWALL_DESIGN_FILE_NAME: 'project.shearwall_design_file_name',
    SHEARWALL_COMPONENT_DESIGN: 'project.shearwall_component_design',
    SPACE_LAYOUT_IMAGE: 'project.space_layout_image',
    SPACE_LAYOUT: 'project.space_layout',
    VECTOR_REFERENCE: 'project.vector_reference',
    DESIGN_LIST: 'project.design_list',
    ADD_DESIGN: 'project.add_design',
    ADD_BEAM_DESIGN: 'project.add_beam_design',
    PLEASE_SELECT_CONDITION_N: 'project.please_select_condition_n',
    ENTER_PROJECT_ID: 'project.enter_project_id',
    PROJECT_ID: 'project.project_id',
    NOT_DESIGNED_YET: 'project.not_designed_yet',
    DOWNLOAD_YJK_MODEL_FILE: 'project.download_yjk_model_file',
    DOWNLOAD_PKPM_MODEL_FILE: 'project.download_pkpm_model_file',
    VIEW_OPT_BEFORE: 'project.view_opt_before',
    VIEW_OPT_AFTER: 'project.view_opt_after',
    DOWNLOAD_OPT_BEFORE_MODEL: 'project.download_opt_before_model',
    DOWNLOAD_OPT_AFTER_MODEL: 'project.download_opt_after_model',
    DOWNLOAD_DIFFUSION_SIZES: 'project.download_diffusion_sizes',
    DOWNLOAD_GNN_SIZES: 'project.download_gnn_sizes',
    PROJECT_TITLE: 'project.project_title',
    SEISMIC_INTENSITY: 'project.seismic_intensity',
    SEISMIC_INTENSITY_WITH_UNIT: 'project.seismic_intensity_with_unit',
    ARCHITECTURAL_FLOOR_PLAN: 'project.architectural_floor_plan',
    BUILDING_STRUCTURE_HEIGHT: 'project.building_structure_height',
    STRUCTURE_TOTAL_HEIGHT_WITH_UNIT: 'project.structure_total_height_with_unit',
    BUILDING_COMPONENT_IMAGE: 'project.building_component_image',
    BUILDING_SPACE_IMAGE: 'project.building_space_image',
    DESIGN_SEISMIC_GROUP: 'project.design_seismic_group',
    EARTHQUAKE_GROUP: 'project.earthquake_group',
    SITE_CATEGORY: 'project.site_category',
    BASIC_WIND_PRESSURE: 'project.basic_wind_pressure',
    WIND_PRESSURE: 'project.wind_pressure',
    GROUND_ROUGHNESS: 'project.ground_roughness',
    STRUCTURE_TOTAL_HEIGHT: 'project.structure_total_height',
    STANDARD_FLOOR_ELEVATION: 'project.standard_floor_elevation',
    STANDARD_FLOOR_WALL_COLUMN_LAYOUT: 'project.standard_floor_wall_column_layout',
    ADD_CORE_TUBE_COMPONENT_DESIGN: 'project.add_core_tube_component_design',
    EDIT_CORE_TUBE_COMPONENT_DESIGN: 'project.edit_core_tube_component_design',
    CORE_TUBE_FILE_DOWNLOAD_OPERATIONS: 'project.core_tube_file_download_operations',
    CORE_TUBE_FILE_DOWNLOAD_INFO: 'project.core_tube_file_download_info',
    COMPONENT_DIMENSION_DIAGRAM: 'project.component_dimension_diagram',
    MODELING_DATA: 'project.modeling_data',
    PLEASE_UPLOAD_CSV_FILE: 'project.please_upload_csv_file',
    INPUT_DATA_VISUALIZATION: 'project.input_data_visualization',
    OUTPUT_IMAGE: 'project.output_image',
    DOWNLOAD_INPUT_DATA_VIS: 'project.download_input_data_vis',
    DOWNLOAD_OUTPUT_DESIGN: 'project.download_output_design',
    COMPONENT_LAYOUT_IMAGE: 'project.component_layout_image',
    COMPONENT_SIZE_IMAGE: 'project.component_size_image',
    MODEL_DATA: 'project.model_data',
    EDIT_CORE_SECTION_DESIGN: 'project.edit_core_section_design',
    INPUT_IMAGE: 'project.input_image',
    CORE_TUBE_PROJECT: 'project.core_tube_project',
    DESIGN_TIME: 'project.design_time',
    DESIGN_DURATION_SECONDS: 'project.design_duration_seconds',
    CREATE_TIME_PROJECT: 'project.create_time',
    ENTER_STANDARD_FLOOR_HEIGHT: 'project.enter_standard_floor_height',
    ENTER_FLOOR_WIDTH: 'project.enter_floor_width',
    ENTER_FLOOR_LENGTH: 'project.enter_floor_length',
    ENTER_MATERIAL_GRADE: 'project.enter_material_grade',
    DESIGN_INFO: 'project.design_info',
    FRAME_DESIGN_FILE_NAME: 'project.frame_design_file_name',
    FRAME_BEAM_DESIGN: 'project.frame_beam_design',
    SECTION_SIZE_DESIGN: 'project.section_size_design',
    MECHANICAL_CALCULATION_RESULTS: 'project.mechanical_calculation_results',
  },

  // ============================================
  // 产品相关 (Product)
  // ============================================
  PRODUCT: {
    CORE_TUBE_PRODUCT_INTRO: 'product.core_tube_product_intro',
    CLICK_TO_BUY: 'product.click_to_buy',
    CORE_TUBE_COMPONENT_DESIGN: 'product.core_tube_component_design',
    BUY_NOW: 'product.buy_now',
  },

  // ============================================
  // 支付相关 (Payment)
  // ============================================
  PAYMENT: {
    WECHAT_PAY: 'payment.wechat_pay',
    PAYMENT_SUCCESS: 'payment.payment_success',
    CONTINUE_PAYMENT: 'payment.continue_payment',
    DELETE_ORDER: 'payment.delete_order',
    PRODUCT_NAME: 'payment.product_name',
    CREATE_TIME: 'payment.create_time',
    PAYMENT_STATUS: 'payment.payment_status',
    UNPAID: 'payment.unpaid',
    PAID: 'payment.paid',
    PAYMENT_AMOUNT: 'payment.payment_amount',
    ORDER_NUMBER: 'payment.order_number',
  },
  
  // ============================================
  // 用户相关 (User)
  // ============================================
  USER: {
    USERNAME: 'user.username',
  },

  // ============================================
  // 案例相关 (Cases)
  // ============================================
  CASES: {
    CASE_INTRO: 'cases.case_intro',
    INPUT_PIXEL_DIAGRAM: 'cases.input_pixel_diagram',
    CLICK_TO_DOWNLOAD_DATA: 'cases.click_to_download_data',
    ENGINEER_DESIGN_PIXEL_DIAGRAM: 'cases.engineer_design_pixel_diagram',
    STRUCTGAN_DESIGN_PIXEL_DIAGRAM: 'cases.StructGAN_design_pixel_diagram',
    STRUCTGAN_DESIGN_MODEL: 'cases.StructGAN_design_model',
    INPUT_DATA: 'cases.input_data',
    ENGINEER_DESIGN: 'cases.engineer_design',
    STRUGAN_DESIGN: 'cases.StruGAN_design',
    INPUT: 'cases.input',
    INTERPRETATION: 'cases.interpretation',
    INPUT_EXAMPLE: 'cases.input_example',
    CASE1_TITLE: 'cases.case1_title',
    CASE1_TIPS: 'cases.case1_tips',
    CASE2_TITLE: 'cases.case2_title',
    CASE2_TIPS: 'cases.case2_tips',
    CASE3_TITLE: 'cases.case3_title',
    CASE3_TIPS: 'cases.case3_tips',
    CASE4_TITLE: 'cases.case4_title',
    CASE4_TIPS: 'cases.case4_tips',
    CASE5_TITLE: 'cases.case5_title',
    CASE5_TIPS: 'cases.case5_tips',
    CASE6_TITLE: 'cases.case6_title',
    CASE6_TIPS: 'cases.case6_tips',
    CASE7_TITLE: 'cases.case7_title',
    CASE7_TIPS: 'cases.case7_tips',
    CASE8_TITLE: 'cases.case8_title',
    CASE8_TIPS: 'cases.case8_tips',
    CASE9_TITLE: 'cases.case9_title',
    CASE9_TIPS: 'cases.case9_tips',
    CASE10_TITLE: 'cases.case10_title',
    CASE10_TIPS: 'cases.case10_tips',
  },

  // ============================================
  // 关于我们 (About)
  // ============================================
  ABOUT: {
    COMPANY_INTRO: 'about.company_intro',
    COMPANY_INTRO_CONTENT1: 'about.company_intro_content1',
    COMPANY_INTRO_CONTENT2: 'about.company_intro_content2',
    COMPANY_INTRO_CONTENT3: 'about.company_intro_content3',
    HEMU_TEAM: 'about.hemu_team',
    HEMU_TEAM_CONTENT: 'about.hemu_team_content',
    HISTORY: 'about.history',
    AWARDS: 'about.awards',
    QUALITY_AND_AWARD: 'about.quality_and_award',
    QUALITY: 'about.quality',
    RESEARCH_PROCESS: 'about.research_process',
  },

  // ============================================
  // 联系方式 (Contact)
  // ============================================
  CONTACT: {
    COOPERATION: 'contact.coopetration',
    COOPERATE: 'contact.cooperate',
    CONTACT_EMAIL: 'contact.contact_email',
    CONTACT_PHONE: 'contact.contact_phone',
    WECHAT: 'contact.wechat',
    PRODUCT_PROBLEM: 'contact.product_problem',
    COMMUNICATION_GROUP: 'contact.communication_group',
    QQ_GROUP: 'contact.QQ_group',
    CASE_NUMBER: 'contact.case_number',
    SUGGEST_CHROME: 'contact.suggest_chrome',
    // 联系表单相关
    BASIC_INFO: 'contact.basic_info',
    NAME: 'contact.name',
    INPUT_NAME: 'contact.input_name',
    UNIT: 'contact.unit',
    INPUT_UNIT: 'contact.input_unit',
    EMAIL: 'contact.email',
    INPUT_EMAIL: 'contact.input_email',
    INPUT_PHONE: 'contact.input_phone',
    QUESTION_DETAIL: 'contact.question_detail',
    INPUT_QUESTION_DETAIL: 'contact.input_question_detail',
    OTHER_SUGGESTION: 'contact.other_suggestion',
    INPUT_SUGGESTION: 'contact.input_suggestion',
    PRODUCT_PLEASE_CONTACT: 'contact.product_please_contact',
    HEMU_PLEASE_CONTACT: 'contact.hemu_please_contact',
    NAME_LENGTH_LIMIT: 'contact.name_length_limit',
    INCORRECT_EMAIL: 'contact.incorrect_email',
    INCORRECT_PHONE: 'contact.incorrect_phone',
    CONTACT_PERSON: 'contact.contact_person',
  },

  // ============================================
  // 新闻资讯 (News)
  // ============================================
  NEWS: {
    LEARN_MORE: 'news.learn_more',
    NEWS_TITLE: 'news.news_title',
    NEWS_CONTENT: 'news.news_content',
    KNOWN_MORE: 'news.known_more',
    MORE_NEWS: 'news.more_news',
    NEWS: 'news.news',
  },

  // ============================================
  // 管理后台相关 (Admin)
  // ============================================
  ADMIN: {
    MENU: {
      DATA_ANALYSIS: 'admin.menu.data_analysis',
      USER_LIST: 'admin.menu.user_list',
      PROJECT_OVERVIEW: 'admin.menu.project_overview',
      ARCHITECTURAL_RENDERING_PROJECT: 'admin.menu.architectural_rendering_project',
      USER_FEEDBACK: 'admin.menu.user_feedback',
      CAMPAIGN_CODE: 'admin.menu.campaign_code',
      VIDEO_MANAGEMENT: 'admin.menu.video_management',
    },
    VIDEO: {
      TITLE: 'admin.video.title',
      SUBTITLE: 'admin.video.subtitle',
      CURRENT_VIDEO_TITLE: 'admin.video.current_video_title',
      UPLOADED_TAG: 'admin.video.uploaded_tag',
      NO_VIDEO: 'admin.video.no_video',
      LOADING: 'admin.video.loading',
      UPLOAD_TITLE: 'admin.video.upload_title',
      SELECT_FILE: 'admin.video.select_file',
      FILE_TIP: 'admin.video.file_tip',
      UPLOADING: 'admin.video.uploading',
      CONFIRM_UPLOAD: 'admin.video.confirm_upload',
      EXCEED_LIMIT: 'admin.video.exceed_limit',
      CONFIRM_DIALOG_TITLE: 'admin.video.confirm_dialog_title',
      CONFIRM_DIALOG_CONTENT: 'admin.video.confirm_dialog_content',
      UPLOAD_SUCCESS: 'admin.video.upload_success',
      UPLOAD_FAILED: 'admin.video.upload_failed',
      UPLOAD_FAILED_NETWORK: 'admin.video.upload_failed_network',
      DELETE: 'admin.video.delete',
      DELETE_CONFIRM_TITLE: 'admin.video.delete_confirm_title',
      DELETE_CONFIRM_CONTENT: 'admin.video.delete_confirm_content',
      DELETE_SUCCESS: 'admin.video.delete_success',
      DELETE_FAILED: 'admin.video.delete_failed',
    },
    CAMPAIGN_CODE: {
      ADD: 'admin.campaign_code.add',
      REFRESH: 'admin.campaign_code.refresh',
      NO_DATA: 'admin.campaign_code.no_data',
      CODE_TEXT: 'admin.campaign_code.code_text',
      POINTS: 'admin.campaign_code.points',
      EFFECTIVE_TIME: 'admin.campaign_code.effective_time',
      EXPIRE_TIME: 'admin.campaign_code.expire_time',
      MAX_USAGE_COUNT: 'admin.campaign_code.max_usage_count',
      USED_COUNT: 'admin.campaign_code.used_count',
      CREATED_AT: 'admin.campaign_code.created_at',
      UNLIMITED: 'admin.campaign_code.unlimited',
      ADD_DIALOG_TITLE: 'admin.campaign_code.add_dialog_title',
      PLACEHOLDER_CODE: 'admin.campaign_code.placeholder_code',
      PLACEHOLDER_POINTS: 'admin.campaign_code.placeholder_points',
      PLACEHOLDER_EFFECTIVE_TIME: 'admin.campaign_code.placeholder_effective_time',
      PLACEHOLDER_EXPIRE_TIME: 'admin.campaign_code.placeholder_expire_time',
      MAX_USAGE_TIP: 'admin.campaign_code.max_usage_tip',
      POINTS_MIN: 'admin.campaign_code.points_min',
      RULE_CODE_REQUIRED: 'admin.campaign_code.rule_code_required',
      RULE_POINTS_REQUIRED: 'admin.campaign_code.rule_points_required',
      RULE_EFFECTIVE_TIME_REQUIRED: 'admin.campaign_code.rule_effective_time_required',
      RULE_EXPIRE_TIME_REQUIRED: 'admin.campaign_code.rule_expire_time_required',
      ADD_SUCCESS: 'admin.campaign_code.add_success',
      ADD_FAILED: 'admin.campaign_code.add_failed',
      LOAD_FAILED: 'admin.campaign_code.load_failed',
      DELETE: 'admin.campaign_code.delete',
      CONFIRM_DELETE: 'admin.campaign_code.confirm_delete',
      DELETE_SUCCESS: 'admin.campaign_code.delete_success',
      DELETE_FAILED: 'admin.campaign_code.delete_failed',
    },
    NEWS: {
      EDIT_NEWS_INFO: 'admin.news.edit_news_info',
      NEWS_TIME_LABEL: 'admin.news.news_time_label',
      NEWS_LINK_LABEL: 'admin.news.news_link_label',
      NEWS_IMAGE_LABEL: 'admin.news.news_image_label',
      ENTER_TITLE: 'admin.news.enter_title',
      ENTER_NEWS_TIME: 'admin.news.enter_news_time',
      ENTER_NEWS_LINK: 'admin.news.enter_news_link',
      UPLOAD_NEWS_IMAGE: 'admin.news.upload_news_image',
      PUBLISH_SUCCESS: 'admin.news.publish_success',
      UPDATE_SUCCESS: 'admin.news.update_success',
      REWRITE: 'admin.news.rewrite',
    },
    VERSION: {
      SORT: 'admin.version.sort',
      SORT_DESC: 'admin.version.sort_desc',
      SORT_ASC: 'admin.version.sort_asc',
      PUBLISH: 'admin.version.publish',
      ADD: 'admin.version.add',
      PUBLISH_SUCCESS: 'admin.version.publish_success',
      PUBLISH_AT: 'admin.version.publish_at',
      VERSION_NUMBER: 'admin.version.version_number',
      VERSION_DESC: 'admin.version.version_desc',
      PUBLISHER: 'admin.version.publisher',
      PUBLISH_TIME: 'admin.version.publish_time',
      EDIT_VERSION_INFO: 'admin.version.edit_version_info',
      PLEASE_ENTER_VERSION_NUMBER: 'admin.version.please_enter_version_number',
      PLEASE_ENTER_ADMIN_NAME: 'admin.version.please_enter_admin_name',
      PLEASE_ENTER_VERSION_DESC: 'admin.version.please_enter_version_desc',
      PLEASE_ENTER_CONTENT_MAX_CHARS: 'admin.version.please_enter_content_max_chars',
      PLEASE_COMPLETE_ALL_FIELDS: 'admin.version.please_complete_all_fields',
      UPLOAD_SUCCESS: 'admin.version.upload_success',
      UPDATE_SUCCESS: 'admin.version.update_success',
      REWRITE: 'admin.version.rewrite',
    },
    MESSAGE: {
      PUBLISHER: 'admin.message.publisher',
      PUBLISH_MESSAGE: 'admin.message.publish_message',
      PLEASE_ENTER_ADMIN_NAME: 'admin.message.please_enter_admin_name',
      PLEASE_ENTER_MESSAGE: 'admin.message.please_enter_message',
      PLEASE_ENTER_MESSAGE_LIMIT: 'admin.message.please_enter_message_limit',
      SEND_SUCCESS: 'admin.message.send_success',
      PLEASE_FILL_ALL_INFO: 'admin.message.please_fill_all_info',
    },
    USER: {
      ENTER_PHONE_USERNAME_ACCOUNT_TO_SEARCH: 'admin.user.enter_phone_username_account_to_search',
      SEARCH_PLACEHOLDER: 'admin.user.search_placeholder',
      RESET: 'admin.user.reset',
      USERNAME: 'admin.user.username',
      NAME: 'admin.user.name',
      COMPANY: 'admin.user.company',
      ACCOUNT: 'admin.user.account',
      ORGANIZATION: 'admin.user.organization',
      PHONE_NUMBER: 'admin.user.phone_number',
      USER_TYPE: 'admin.user.user_type',
      PASSWORD: 'admin.user.password',
      ACTIONS: 'admin.user.actions',
      REGISTERED_AT: 'admin.user.registered_at',
      SUBSCRIPTION_START_DATE: 'admin.user.subscription_start_date',
      SORT_BY: 'admin.user.sort_by',
      SORT_BY_REGISTERED: 'admin.user.sort_by_registered',
      SORT_BY_SUBSCRIPTION_START: 'admin.user.sort_by_subscription_start',
      SORT_ORDER: 'admin.user.sort_order',
      SORT_ORDER_NEWEST: 'admin.user.sort_order_newest',
      SORT_ORDER_OLDEST: 'admin.user.sort_order_oldest',
      TOTAL_PROJECTS: 'admin.user.total_projects',
      CUMULATIVE_DESIGN_DURATION_SECONDS: 'admin.user.cumulative_design_duration_seconds',
      VIEW_PROJECTS: 'admin.user.view_projects',
      VIEW: 'admin.user.view',
      MODIFY_PERMISSIONS: 'admin.user.modify_permissions',
      MODIFY_USER_PERMISSIONS: 'admin.user.modify_user_permissions',
      AUTHORITY_TYPE: 'admin.user.authority_type',
      OPTION_GENERAL_USER: 'admin.user.option_general_user',
      OPTION_ADMIN: 'admin.user.option_admin',
      OPTION_MH_MEMBER: 'admin.user.option_mh_member',
      OPTION_MH_PARTNER: 'admin.user.option_mh_partner',
      RENDER_ADMIN: 'admin.user.render_admin',
      RENDER_ADMIN_HINT: 'admin.user.render_admin_hint',
      SET_RENDER_ADMIN: 'admin.user.set_render_admin',
      CANCEL_RENDER_ADMIN: 'admin.user.cancel_render_admin',
      SET_RENDER_ADMIN_SUCCESS: 'admin.user.set_render_admin_success',
      CANCEL_RENDER_ADMIN_SUCCESS: 'admin.user.cancel_render_admin_success',
      OPTION_SUPER_ADMIN: 'admin.user.option_super_admin',
      FILTER_USER_TYPE: 'admin.user.filter_user_type',
      // 新增字段
      ADDRESS: 'admin.user.address',
      EMAIL: 'admin.user.email',
      CREDIT_CARD: 'admin.user.credit_card',
      // 删除用户
      DELETE_USER: 'admin.user.delete_user',
      DELETE_USER_CONFIRM: 'admin.user.delete_user_confirm',
      DELETE_USER_SUCCESS: 'admin.user.delete_user_success',
      // 钱包封禁/解封
      BAN_WALLET: 'admin.user.ban_wallet',
      BAN_WALLET_SUCCESS: 'admin.user.ban_wallet_success',
      BAN_WALLET_CONFIRM: 'admin.user.ban_wallet_confirm',
      UNBAN_WALLET: 'admin.user.unban_wallet',
      UNBAN_WALLET_SUCCESS: 'admin.user.unban_wallet_success',
      UNBAN_WALLET_CONFIRM: 'admin.user.unban_wallet_confirm',
      WALLET_STATUS: 'admin.user.wallet_status',
      ACCOUNT_STATUS: 'admin.user.account_status',
      MONTHLY_POINTS: 'admin.user.monthly_points',
      PERMANENT_POINTS: 'admin.user.permanent_points',
      SUBSCRIPTION_STATUS: 'admin.user.subscription_status',
      WALLET_BANNED: 'admin.user.wallet_banned',
      WALLET_ACTIVE: 'admin.user.wallet_active',
      BAN_ACCOUNT: 'admin.user.ban_account',
      UNBAN_ACCOUNT: 'admin.user.unban_account',
      BAN_ACCOUNT_CONFIRM: 'admin.user.ban_account_confirm',
      UNBAN_ACCOUNT_CONFIRM: 'admin.user.unban_account_confirm',
      BAN_ACCOUNT_SUCCESS: 'admin.user.ban_account_success',
      UNBAN_ACCOUNT_SUCCESS: 'admin.user.unban_account_success',
      ADD_MONTHLY_POINTS: 'admin.user.adjust_monthly_points',
      ADD_PERMANENT_POINTS: 'admin.user.adjust_permanent_points',
      ENTER_POINTS: 'admin.user.enter_points',
      POINTS_MUST_BE_POSITIVE: 'admin.user.points_must_be_positive',
      INCREASE: 'admin.user.increase',
      DECREASE: 'admin.user.decrease',
      // 成功消息
      MODIFY_PASSWORD_SUCCESS: 'admin.user.modify_password_success',
      MODIFY_AUTHORITY_SUCCESS: 'admin.user.modify_authority_success',
      MODIFY_USER_INFO_SUCCESS: 'admin.user.modify_user_info_success',
      MODIFY_USER_INFO_FAILED: 'admin.user.modify_user_info_failed',
      INCREASE_MONTHLY_POINTS_SUCCESS: 'admin.user.increase_monthly_points_success',
      DECREASE_MONTHLY_POINTS_SUCCESS: 'admin.user.decrease_monthly_points_success',
      INCREASE_PERMANENT_POINTS_SUCCESS: 'admin.user.increase_permanent_points_success',
      DECREASE_PERMANENT_POINTS_SUCCESS: 'admin.user.decrease_permanent_points_success',
      ADJUST_QUOTA_LIMITS: 'admin.user.adjust_quota_limits',
      ADJUST_QUOTA_LIMITS_TITLE: 'admin.user.adjust_quota_limits_title',
      QUOTA_LIMIT_HINT: 'admin.user.quota_limit_hint',
      INVALID_QUOTA_LIMIT: 'admin.user.invalid_quota_limit',
      UPDATE_QUOTA_LIMITS_SUCCESS: 'admin.user.update_quota_limits_success',
      UPDATE_QUOTA_LIMITS_FAILED: 'admin.user.update_quota_limits_failed',
    },
    PROJECT: {
      TITLE: 'admin.project.title',
      SEARCH_PLACEHOLDER: 'admin.project.search_placeholder',
      RESET: 'admin.project.reset',
      NO_PROJECT_DATA: 'admin.project.no_project_data',
      PROJECT_ID: 'admin.project.project_id',
      USER: 'admin.project.user',
      CREATE_TIME: 'admin.project.create_time',
      IDEMPOTENCY: 'admin.project.idempotency',
      ORDER: 'admin.project.order',
      INPUT_INFO: 'admin.project.input_info',
      TEXT_REQUIREMENT: 'admin.project.text_requirement',
      STYLE_SETTING: 'admin.project.style_setting',
      OUTPUT_SIZE: 'admin.project.output_size',
      TIME_PERIOD: 'admin.project.time_period',
      MAX_IMAGES: 'admin.project.max_images',
      INPUT_REFERENCE_IMAGES: 'admin.project.input_reference_images',
      LOAD_FAILED: 'admin.project.load_failed',
      DESCRIPTIVE_IMAGE: 'admin.project.descriptive_image',
      MAIN_REFERENCE_IMAGE: 'admin.project.main_reference_image',
      BACKGROUND_REFERENCE_IMAGE: 'admin.project.background_reference_image',
      RENDER_RESULTS: 'admin.project.render_results',
      LOADING: 'admin.project.loading',
      NO_DESIGN_RESULTS: 'admin.project.no_design_results',
      FETCH_PROJECT_DATA_FAILED: 'admin.project.fetch_project_data_failed',
      PROJECT_DATA_LOAD_FAILED: 'admin.project.project_data_load_failed',
    },
    USER_FEEDBACK: {
      FIELDS: {
        USER_REQUEST: 'admin.user_feedback.fields.user_request',
        RESOLUTION: 'admin.user_feedback.fields.resolution',
        ASPECT_RATIO: 'admin.user_feedback.fields.aspect_ratio',
        ANGLE: 'admin.user_feedback.fields.angle',
        WALL_MATERIAL: 'admin.user_feedback.fields.wall_material',
        WALL_WIDTH: 'admin.user_feedback.fields.wall_width',
        WALL_DIRECTION: 'admin.user_feedback.fields.wall_direction',
        WALL_COLOR: 'admin.user_feedback.fields.wall_color',
        WINDOW_MATERIAL: 'admin.user_feedback.fields.window_material',
        WINDOW_COLOR: 'admin.user_feedback.fields.window_color',
        BACKGROUND_NAME: 'admin.user_feedback.fields.background_name',
        TIME_PERIOD: 'admin.user_feedback.fields.time_period',
      },
      IMAGES: {
        MAIN_REFERENCE: 'admin.user_feedback.images.main_reference',
        BACKGROUND_REFERENCE: 'admin.user_feedback.images.background_reference',
        WALL_REFERENCE: 'admin.user_feedback.images.wall_reference',
        REFERENCE_1: 'admin.user_feedback.images.reference_1',
        REFERENCE_2: 'admin.user_feedback.images.reference_2',
        REFERENCE_3: 'admin.user_feedback.images.reference_3',
      },
      SECTIONS: {
        GENERATED_IMAGE: 'admin.user_feedback.sections.generated_image',
      },
    },
  },

  // ============================================
  // 业务相关 (Business)
  // ============================================
  BUSINESS: {
    BUSINESS_CONTENT: 'business.business_content',
    BUSINESS_TITLE: 'business.business_title',
    BUSINESS_CONTENT1: 'business.business_content1',
    BUSINESS_CONTENT2: 'business.business_content2',
    BUSINESS_CONTENT3: 'business.business_content3',
  },

  // ============================================
  // 建筑渲染（Render / Make House）
  // ============================================
  RENDER: {
    HEADER_TITLE: 'render.header_title',
    HEADER_AI_TITLE: 'render.header_ai_title',
    HEADER_SUBTITLE: 'render.header_subtitle',

    INPUT_SECTION_TITLE: 'render.input_section_title',
    INPUT_SECTION_DESC: 'render.input_section_desc',

    UPLOAD_HOUSE_IMAGE: 'render.upload_house_image',
    BACKGROUND_IMAGE: 'render.background_image',
    UPLOAD_COLLAPSE_LABEL: 'render.upload_collapse_label',
    UPLOAD_EXPAND_LABEL: 'render.upload_expand_label',
    DETAILED_INSTRUCTION: 'render.detailed_instruction',
    CONTENT_OTHER_TITLE: 'render.content_other_title',
    CONTENT_WALL_TITLE: 'render.content_wall_title',
    CONTENT_WINDOW_TITLE: 'render.content_window_title',
    WINDOW_MATERIAL: 'render.window_material',
    WINDOW_COLOR: 'render.window_color',
    WALL_MATERIAL: 'render.wall_material',
    WALL_WIDTH: 'render.wall_width',
    WALL_DIRECTION: 'render.wall_direction',
    WALL_COLOR: 'render.wall_color',
    NONE: 'render.none',
    OPEN_COLOR_PICKER: 'render.open_color_picker',
    RESET_COLOR: 'render.reset_color',
    
    // 外壁材质选项
    WALL_MATERIAL_GALVALUME: 'render.wall_material_galvalume',
    WALL_MATERIAL_WOOD_GRAIN: 'render.wall_material_wood_grain',
    WALL_MATERIAL_METAL_SIDING: 'render.wall_material_metal_siding',
    WALL_MATERIAL_BRICK_BLOCK: 'render.wall_material_brick_block',
    WALL_MATERIAL_STONE_TILE: 'render.wall_material_stone_tile',
    WALL_MATERIAL_BORDER_LINE: 'render.wall_material_border_line',
    WALL_MATERIAL_PAINTED_WALL: 'render.wall_material_painted_wall',
    
    // 外壁宽度选项
    WALL_WIDTH_WIDE: 'render.wall_width_wide',
    WALL_WIDTH_NARROW: 'render.wall_width_narrow',
    WALL_WIDTH_NORMAL: 'render.wall_width_normal',
    
    // 外壁方向选项
    WALL_DIRECTION_HORIZONTAL: 'render.wall_direction_horizontal',
    WALL_DIRECTION_VERTICAL: 'render.wall_direction_vertical',
    
    // 窗户材质选项
    WINDOW_MATERIAL_METAL: 'render.window_material_metal',
    WINDOW_MATERIAL_RESIN: 'render.window_material_resin',
    WINDOW_MATERIAL_WOOD: 'render.window_material_wood',
    
    DRAG_HOUSE_INSTRUCTION: 'render.drag_house_instruction',
    BROWSE_TEXT: 'render.browse_text',
    SUPPORT_FORMATS: 'render.support_formats',
    PASTE_IMAGE: 'render.paste_image',
    CLIPBOARD_NO_IMAGE: 'render.clipboard_no_image',
    CLIPBOARD_READ_FAILED: 'render.clipboard_read_failed',

    TIME_SECTION_TITLE: 'render.time_section_title',
    TIME_MORNING_LABEL: 'render.time_morning_label',
    TIME_MORNING_DESC: 'render.time_morning_desc',
    TIME_AFTERNOON_LABEL: 'render.time_afternoon_label',
    TIME_AFTERNOON_DESC: 'render.time_afternoon_desc',
    TIME_EVENING_LABEL: 'render.time_evening_label',
    TIME_EVENING_DESC: 'render.time_evening_desc',
    TIME_NIGHT_LABEL: 'render.time_night_label',
    TIME_NIGHT_DESC: 'render.time_night_desc',
    TIME_MIDSUMMER_NOON_LABEL: 'render.time_midsummer_noon_label',
    TIME_MIDSUMMER_NOON_DESC: 'render.time_midsummer_noon_desc',
    TIME_CLOUDY_DAY_LABEL: 'render.time_cloudy_day_label',
    TIME_CLOUDY_DAY_DESC: 'render.time_cloudy_day_desc',
    TIME_NIGHT_CITYSCAPE_LABEL: 'render.time_night_cityscape_label',
    TIME_NIGHT_CITYSCAPE_DESC: 'render.time_night_cityscape_desc',
    TIME_MINIMALIST_GRAY_LABEL: 'render.time_minimalist_gray_label',
    TIME_MINIMALIST_GRAY_DESC: 'render.time_minimalist_gray_desc',
    TIME_OVERCAST_GRAY_LABEL: 'render.time_overcast_gray_label',
    TIME_OVERCAST_GRAY_DESC: 'render.time_overcast_gray_desc',
    TIME_RAINY_GRAY_LABEL: 'render.time_rainy_gray_label',
    TIME_RAINY_GRAY_DESC: 'render.time_rainy_gray_desc',
    TIME_SPRING_DAY_LABEL: 'render.time_spring_day_label',
    TIME_SPRING_DAY_DESC: 'render.time_spring_day_desc',
    TIME_BLUE_HOUR_LABEL: 'render.time_blue_hour_label',
    TIME_BLUE_HOUR_DESC: 'render.time_blue_hour_desc',
    TIME_POST_RAIN_BLUE_HOUR_LABEL: 'render.time_post_rain_blue_hour_label',
    TIME_POST_RAIN_BLUE_HOUR_DESC: 'render.time_post_rain_blue_hour_desc',
    TIME_SUNSET_GLOW_LABEL: 'render.time_sunset_glow_label',
    TIME_SUNSET_GLOW_DESC: 'render.time_sunset_glow_desc',
    TIME_CLEAR_TONE_LABEL: 'render.time_clear_tone_label',
    TIME_CLEAR_TONE_DESC: 'render.time_clear_tone_desc',
    TIME_GOLDEN_HOUR_LABEL: 'render.time_golden_hour_label',
    TIME_GOLDEN_HOUR_DESC: 'render.time_golden_hour_desc',
    TIME_CHAMPAGNE_GOLD_LABEL: 'render.time_champagne_gold_label',
    TIME_CHAMPAGNE_GOLD_DESC: 'render.time_champagne_gold_desc',
    TIME_BRIGHT_RAINY_SCENE_LABEL: 'render.time_bright_rainy_scene_label',
    TIME_BRIGHT_RAINY_SCENE_DESC: 'render.time_bright_rainy_scene_desc',

    ADD_BACKGROUND_IMAGES: 'render.add_background_images',

    RENDER_INSTRUCTIONS_TITLE: 'render.render_instructions_title',
    CUSTOMIZE_INSTRUCTION_TITLE: 'render.customize_instruction_title',
    RENDER_INSTRUCTIONS_PLACEHOLDER: 'render.render_instructions_placeholder',
    RENDER_INSTRUCTIONS_PLACEHOLDER_LINE1: 'render.render_instructions_placeholder_line1',
    RENDER_INSTRUCTIONS_PLACEHOLDER_LINE2: 'render.render_instructions_placeholder_line2',
    INTERIOR_RENDER_INSTRUCTIONS_PLACEHOLDER_LINE2: 'render.interior_render_instructions_placeholder_line2',
    QUICK_TEMPLATES_TITLE: 'render.quick_templates_title',
    QUICK_TEMPLATE_1: 'render.quick_template_1',
    QUICK_TEMPLATE_2: 'render.quick_template_2',
    QUICK_TEMPLATE_3: 'render.quick_template_3',
    QUICK_TEMPLATE_4: 'render.quick_template_4',

    SUBMIT_START: 'render.submit_start',
    SUBMIT_SUBMITTING: 'render.submit_submitting',
    SUBMIT_SUBMITTING_DO_NOT_CLOSE: 'render.submit_submitting_do_not_close',
    DESIGNING: 'render.designing',
    COST_POINTS_LABEL: 'render.cost_points_label',
    COIN_COST_LABEL: 'render.coin_cost_label',
    COIN_COST_LOADING: 'render.coin_cost_loading',
    COIN_COST_UNKNOWN: 'render.coin_cost_unknown',
    IMAGE_RESOURCE_UNIT: 'render.image_resource_unit',
    IMAGE_RESOURCE_POINT: 'render.image_resource_point',
    ORDER_STATUS_LABEL: 'render.order_status_label',

    OUTPUT_SECTION_TITLE: 'render.output_section_title',
    OUTPUT_SECTION_DESC: 'render.output_section_desc',
    LOADING_TITLE: 'render.loading_title',
    LOADING_DESC: 'render.loading_desc',
    UPSCALE_RUNNING: 'render.upscale_running',
    PLACEHOLDER_TITLE: 'render.placeholder_title',
    GUIDE_UPLOAD_HOUSE: 'render.guide_upload_house',
    GUIDE_SELECT_TIME: 'render.guide_select_time',
    GUIDE_ADD_BACKGROUNDS: 'render.guide_add_backgrounds',
    GUIDE_START_RENDERING: 'render.guide_start_rendering',

    GENERATED_DESC: 'render.generated_desc',
    DOWNLOAD_IMAGE: 'render.download_image',
    DOWNLOAD_QUALITY_NORMAL: 'render.download_quality_normal',
    DOWNLOAD_QUALITY_HIGH: 'render.download_quality_high',
    DOWNLOAD_QUALITY_ULTRA: 'render.download_quality_ultra',
    DOWNLOAD_QUALITY_POINTS: 'render.download_quality_points',
    DOWNLOAD_QUALITY_NORMAL_TOOLTIP: 'render.download_quality_normal_tooltip',
    DOWNLOAD_QUALITY_HIGH_TOOLTIP: 'render.download_quality_high_tooltip',
    DOWNLOAD_QUALITY_ULTRA_TOOLTIP: 'render.download_quality_ultra_tooltip',
    EMBEDDED_RESULTS_TITLE: 'render.embedded_results_title',
    EMBEDDED_SETTINGS_TITLE: 'render.embedded_settings_title',
    EMBEDDED_QUALITY_ORIGINAL: 'render.embedded_quality_original',
    EMBEDDED_SELECTED_COUNT: 'render.embedded_selected_count',
    EMBEDDED_SELECT_HINT: 'render.embedded_select_hint',
    EMBEDDED_SELECT_ALL: 'render.embedded_select_all',
    EMBEDDED_SELECT_IMAGE: 'render.embedded_select_image',
    EMBEDDED_CLEAR_SELECTION: 'render.embedded_clear_selection',
    EMBEDDED_DOWNLOAD_SELECTED: 'render.embedded_download_selected',
    EMBEDDED_RESULTS_EMPTY: 'render.embedded_results_empty',
    EMBEDDED_DOWNLOADING_PROGRESS: 'render.embedded_downloading_progress',
    EMBEDDED_UPSCALING_PROGRESS: 'render.embedded_upscaling_progress',
    EMBEDDED_CAPTURING: 'render.embedded_capturing',
    EMBEDDED_CAPTURE_FAILED: 'render.embedded_capture_failed',
    FAVORITE_BUTTON: 'render.favorite_button',
    DOWNLOAD_QUALITY_UPSCALE_TODO: 'render.download_quality_upscale_todo',
    DRAWING_TEXT_PRESET_LDK: 'render.drawing_text_preset_ldk',
    DRAWING_TEXT_PRESET_MASTER_BEDROOM: 'render.drawing_text_preset_master_bedroom',
    DRAWING_TEXT_PRESET_BEDROOM: 'render.drawing_text_preset_bedroom',
    DRAWING_TEXT_PRESET_ENTRANCE: 'render.drawing_text_preset_entrance',
    DRAWING_TEXT_PRESET_PORCH: 'render.drawing_text_preset_porch',
    DRAWING_TEXT_PRESET_TOILET: 'render.drawing_text_preset_toilet',
    DRAWING_TEXT_PRESET_BATHROOM: 'render.drawing_text_preset_bathroom',
    DRAWING_TEXT_PRESET_DRESSING_ROOM: 'render.drawing_text_preset_dressing_room',
    DRAWING_TEXT_PRESET_WASHROOM: 'render.drawing_text_preset_washroom',
    DRAWING_TEXT_PRESET_WIC: 'render.drawing_text_preset_wic',
    DRAWING_TEXT_PRESET_ATRIUM: 'render.drawing_text_preset_atrium',
    SHARE_RESULT: 'render.share_result',
    SHARE_TODO: 'render.share_todo',
    ALT_HOUSE_PREVIEW: 'render.alt_house_preview',
    ALT_BACKGROUND_PREVIEW: 'render.alt_background_preview',
    ALT_RENDER_PLACEHOLDER: 'render.alt_render_placeholder',
    ALT_RENDER_RESULT: 'render.alt_render_result',

    DIALOG_TITLE: 'render.dialog_title',
    DIALOG_LOADING_SPINNER: 'render.dialog_loading_spinner',
    DIALOG_LOADING_DESC: 'render.dialog_loading_desc',
    DIALOG_SUCCESS: 'render.dialog_success',
    DIALOG_FAILED: 'render.dialog_failed',
    DIALOG_BACK_TO_LIST: 'render.dialog_back_to_list',
    DIALOG_CANCEL: 'render.dialog_cancel',
    DIALOG_DOWNLOAD_RESULT: 'render.dialog_download_result',
    ONLY_IMAGE_FILE: 'render.only_image_file',
    IMAGE_SIZE_MAX_50M: 'render.image_size_max_50M',

    ERROR_SUBMIT: 'render.error_submit',
    ERROR_RESULT: 'render.error_result',
    ERROR_DESIGN_BUSY: 'render.error_design_busy',
    ERROR_CREATE_FIRST: 'render.error_create_first',
    ERROR_PRICING_MISSING: 'render.error_pricing_missing',
    ERROR_HOUSE_IMAGE_LOAD_FAILED: 'render.error_house_image_load_failed',
    ERROR_BACKGROUND_IMAGE_LOAD_FAILED: 'render.error_background_image_load_failed',
    ERROR_WALL_IMAGE_LOAD_FAILED: 'render.error_wall_image_load_failed',
    ERROR_ORDER_ID_MISSING: 'render.error_order_id_missing',

    VALID_PROJECT_ID_REQUIRED: 'render.valid_project_id_required',
    VALID_UPLOAD_HOUSE: 'render.valid_upload_house',
    VALID_UPLOAD_INTERIOR_INPUT: 'render.valid_upload_interior_input',
    VALID_UPLOAD_BACKGROUND: 'render.valid_upload_background',
    VALID_SELECT_TIME: 'render.valid_select_time',
    VALID_PROMPT_MIN: 'render.valid_prompt_min',

    // 风格选项（UI 显示文本）
    STYLE_REALISTIC_LABEL: 'render.style_realistic_label',
    STYLE_REALISTIC_DESC: 'render.style_realistic_desc',
    STYLE_PENCIL_LABEL: 'render.style_pencil_label',
    STYLE_PENCIL_DESC: 'render.style_pencil_desc',
    STYLE_MARKER_LABEL: 'render.style_marker_label',
    STYLE_MARKER_DESC: 'render.style_marker_desc',
    STYLE_WATERCOLOR_LABEL: 'render.style_watercolor_label',
    STYLE_WATERCOLOR_DESC: 'render.style_watercolor_desc',
    STYLE_MODEL_LABEL: 'render.style_model_label',
    STYLE_MODEL_DESC: 'render.style_model_desc',

    // 生成数量选项
    COUNT_ONE_LABEL: 'render.count_one_label',
    COUNT_ONE_DESC: 'render.count_one_desc',
    COUNT_THREE_LABEL: 'render.count_three_label',
    COUNT_THREE_DESC: 'render.count_three_desc',
    COUNT_FIVE_LABEL: 'render.count_five_label',
    COUNT_FIVE_DESC: 'render.count_five_desc',

    // 默认背景名称
    BG_MOUNTAIN_NAME: 'render.bg_mountain_name',
    BG_URBAN_NAME: 'render.bg_urban_name',
    BG_FOREST_NAME: 'render.bg_forest_name',
    BG_MOUNTAIN_DESC: 'render.bg_mountain_desc',
    BG_URBAN_DESC: 'render.bg_urban_desc',
    BG_FOREST_DESC: 'render.bg_forest_desc',

    // 卡片标题
    COUNT_SECTION_TITLE: 'render.count_section_title',
    STYLE_SECTION_TITLE: 'render.style_section_title',
    ASPECT_RATIO_SECTION_TITLE: 'render.aspect_ratio_section_title',
    DETAIL_SETTINGS_LABEL: 'render.detail_settings_label',
    DETAIL_IMAGE_COUNT_LABEL: 'render.detail_image_count_label',
    DETAIL_RESOLUTION_LABEL: 'render.detail_resolution_label',
    DETAIL_ANGLE_LABEL: 'render.detail_angle_label',
    ANGLE_SAME: 'render.angle_same',
    ANGLE_FRONT: 'render.angle_front',
    ANGLE_DIAGONAL: 'render.angle_diagonal',
    RATIO_HEADER_LABEL: 'render.ratio_header_label',
    
    // 背景图片相关
    BG_CHOOSE_DEFAULT: 'render.bg_choose_default',
    BG_UPLOAD_OWN: 'render.bg_upload_own',
    BG_CUSTOM_PLACEHOLDER_TITLE: 'render.bg_custom_placeholder_title',
    BG_CUSTOM_PLACEHOLDER_DESC: 'render.bg_custom_placeholder_desc',

    // 绘制编辑相关
    DRAW_EDIT_AREA_BUTTON: 'render.draw_edit_area_button',
    CROP_INPUT_BUTTON: 'render.crop_input_button',
    CROP_DIALOG_TITLE: 'render.crop_dialog_title',
    CROP_DIALOG_TITLE_PDF: 'render.crop_dialog_title_pdf',
    CROP_DIALOG_PAGES: 'render.crop_dialog_pages',
    CROP_DIALOG_HINT: 'render.crop_dialog_hint',
    CROP_DIALOG_RESET: 'render.crop_dialog_reset',
    CROP_DIALOG_APPLY: 'render.crop_dialog_apply',
    CROP_DIALOG_FAILED: 'render.crop_dialog_failed',
    CROP_DIALOG_LOADING: 'render.crop_dialog_loading',
    DELETE_MASK_BUTTON: 'render.delete_mask_button',
    
    // 长宽比相关
    ASPECT_RATIO_SUBTITLE: 'render.aspect_ratio_subtitle',
    ASPECT_RATIO_PRINT_LANDSCAPE: 'render.aspect_ratio_print_landscape',
    ASPECT_RATIO_PRESENTATION: 'render.aspect_ratio_presentation',
    ASPECT_RATIO_SQUARE_FEED: 'render.aspect_ratio_square_feed',
    ASPECT_RATIO_PRINT_PORTRAIT: 'render.aspect_ratio_print_portrait',
    ASPECT_RATIO_VERTICAL_REEL: 'render.aspect_ratio_vertical_reel',
    
    // 参考图相关
    REFERENCE_IMAGE_SUBTITLE: 'render.reference_image_subtitle',
    REFERENCE_IMAGE_PLACEHOLDER: 'render.reference_image_placeholder',
    REFERENCE_IMAGE_TITLE_ONLY: 'render.reference_image_title_only',
    REFERENCE_IMAGE_OPTIONAL: 'render.reference_image_optional',
    REFERENCE_IMAGE_MAX_WARNING: 'render.reference_image_max_warning',

    // 分辨率提示
    RESOLUTION_TOOLTIP_1K: 'render.resolution_tooltip_1k',
    RESOLUTION_TOOLTIP_2K: 'render.resolution_tooltip_2k',
    RESOLUTION_TOOLTIP_4K: 'render.resolution_tooltip_4k',
    
    // 结果操作按钮
    USE_AS_INPUT: 'render.use_as_input',
    EDIT_RESULT_IMAGE_BUTTON: 'render.edit_result_image_button',
    OBJECT_ROTATE_TOOL: 'render.object_rotate_tool',
    OBJECT_ROTATE_DRAG_HINT: 'render.object_rotate_drag_hint',
    OBJECT_ROTATE_NO_PLATE: 'render.object_rotate_no_plate',
    OBJECT_ROTATE_PLATE_KEPT: 'render.object_rotate_plate_kept',
    OBJECT_ROTATE_SCANNING: 'render.object_rotate_scanning',
    OBJECT_ROTATE_APPLIED: 'render.object_rotate_applied',
    OBJECT_ROTATE_PICK_HINT: 'render.object_rotate_pick_hint',
    OBJECT_ROTATE_PREPARING: 'render.object_rotate_preparing',
    OBJECT_ROTATE_PREPARED: 'render.object_rotate_prepared',
    OBJECT_ROTATE_BRING_FORWARD: 'render.object_rotate_bring_forward',
    OBJECT_ROTATE_SEND_BACKWARD: 'render.object_rotate_send_backward',
    OBJECT_ROTATE_BUILDING: 'render.object_rotate_building',
    OBJECT_ROTATE_FAILED: 'render.object_rotate_failed',
    OBJECT_ROTATE_RESELECT: 'render.object_rotate_reselect',
    OBJECT_ROTATE_MODE_MOVE: 'render.object_rotate_mode_move',
    OBJECT_ROTATE_MODE_ROTATE: 'render.object_rotate_mode_rotate',
    OBJECT_ROTATE_MODE_SCALE: 'render.object_rotate_mode_scale',
    OBJECT_ROTATE_PERSPECTIVE: 'render.object_rotate_perspective',
    OBJECT_ROTATE_RESET: 'render.object_rotate_reset',
    OBJECT_ROTATE_APPLY: 'render.object_rotate_apply',
    FEEDBACK_ISSUE: 'render.feedback_issue',
    FEEDBACK_IMAGE_TITLE: 'render.feedback_image_title',
    FEEDBACK_DESCRIPTION_LABEL: 'render.feedback_description_label',
    FEEDBACK_DESCRIPTION_PLACEHOLDER: 'render.feedback_description_placeholder',
    FEEDBACK_DESCRIPTION_REQUIRED: 'render.feedback_description_required',
    FEEDBACK_SUBMIT: 'render.feedback_submit',
    GENERATE_TIME_LAPSE_VIDEO_BUTTON: 'render.generate_time_lapse_video_button',
    GENERATE_TIME_LAPSE_VIDEO_POINTS: 'render.generate_time_lapse_video_points',
    DOWNLOAD_ALL: 'render.download_all',
    RESULT_SENT_TO_INPUT: 'render.result_sent_to_input',
    NO_RESULT_TO_SEND: 'render.no_result_to_send',
    FAILED_SEND_RESULT_TO_INPUT: 'render.failed_send_result_to_input',
    FEEDBACK_SUBMIT_SUCCESS: 'render.feedback_submit_success',
    FEEDBACK_SUBMIT_FAILED: 'render.feedback_submit_failed',
    
    // 历史记录
    RECENT_GENERATIONS: 'render.recent_generations',
    GENERATED_RESULT: 'render.generated_result',
    HISTORY_EMPTY: 'render.history_empty',
    HISTORY_TAB_INPUT: 'render.history_tab_input',
    HISTORY_TAB_OUTPUT: 'render.history_tab_output',
    HISTORY_PREVIEW_TITLE: 'render.history_preview_title',
    PROMPT_DOWNLOAD_TITLE: 'render.prompt_download_title',
    PROMPT_DOWNLOAD_DESC: 'render.prompt_download_desc',
    PROMPT_DOWNLOAD_BUTTON: 'render.prompt_download_button',
    PROMPT_DOWNLOAD_SUCCESS: 'render.prompt_download_success',
    PROMPT_DOWNLOAD_EMPTY: 'render.prompt_download_empty',
    
    // 收藏相关消息
    FAVORITE_ADD_SUCCESS: 'render.favorite_add_success',
    FAVORITE_REMOVE_SUCCESS: 'render.favorite_remove_success',
    FAVORITE_ADD_FAILED: 'render.favorite_add_failed',
    FAVORITE_REMOVE_FAILED: 'render.favorite_remove_failed',

    // 删除生成结果图
    RESULT_DELETE_BUTTON: 'render.result_delete_button',
    RESULT_DELETE_CONFIRM: 'render.result_delete_confirm',
    RESULT_DELETE_SUCCESS: 'render.result_delete_success',
    RESULT_DELETE_FAILED: 'render.result_delete_failed',
    
    // AI 助手
    AI_ASSISTANT_GREETING: 'render.ai_assistant_greeting',
    AI_ASSISTANT_PLACEHOLDER: 'render.ai_assistant_placeholder',
    QA_FLOAT_BALL_LABEL: 'render.qa_float_ball_label',

    // 内装模式上传入口
    INTERIOR_FLOORPLAN_TITLE: 'render.interior_floorplan_title',
    INTERIOR_FLOORPLAN_HEADING: 'render.interior_floorplan_heading',
    INTERIOR_ROOM_TITLE: 'render.interior_room_title',
    INTERIOR_ROOM_HEADING: 'render.interior_room_heading',
    INTERIOR_UPLOAD_METHODS: 'render.interior_upload_methods',
    INTERIOR_UPLOAD_FORMATS: 'render.interior_upload_formats',
    INTERIOR_OR_DIVIDER: 'render.interior_or_divider',

    // 内装スタイル選択
    INTERIOR_STYLE_TITLE: 'render.interior_style_title',
    INTERIOR_STYLE_JAPANDI: 'render.interior_style_japandi',
    INTERIOR_STYLE_NATURAL: 'render.interior_style_natural',
    INTERIOR_STYLE_SIMPLE_MODERN: 'render.interior_style_simple_modern',
    INTERIOR_STYLE_VINTAGE: 'render.interior_style_vintage',
    INTERIOR_STYLE_EMPTY: 'render.interior_style_empty',
    INTERIOR_STYLE_NONE: 'render.interior_style_none',
    INTERIOR_STYLE_NONE_DESC: 'render.interior_style_none_desc',
    INTERIOR_STYLE_CUSTOM: 'render.interior_style_custom',
    INTERIOR_STYLE_CUSTOM_PLACEHOLDER_TITLE: 'render.interior_style_custom_placeholder_title',
    INTERIOR_STYLE_CUSTOM_PLACEHOLDER_DESC: 'render.interior_style_custom_placeholder_desc',
    INTERIOR_STYLE_CANVAS_TITLE: 'render.interior_style_canvas_title',
    INTERIOR_STYLE_CANVAS_SAVE: 'render.interior_style_canvas_save',
    INTERIOR_STYLE_CANVAS_CANCEL: 'render.interior_style_canvas_cancel',
    INTERIOR_STYLE_CANVAS_UPLOAD: 'render.interior_style_canvas_upload',
    INTERIOR_STYLE_CANVAS_ADD_TEXT: 'render.interior_style_canvas_add_text',
    INTERIOR_STYLE_CANVAS_DELETE: 'render.interior_style_canvas_delete',
    INTERIOR_STYLE_CANVAS_FORWARD: 'render.interior_style_canvas_forward',
    INTERIOR_STYLE_CANVAS_BACKWARD: 'render.interior_style_canvas_backward',
    INTERIOR_STYLE_CANVAS_FIT: 'render.interior_style_canvas_fit',
    INTERIOR_STYLE_CANVAS_HINT: 'render.interior_style_canvas_hint',
    INTERIOR_STYLE_CANVAS_DEFAULT_TEXT: 'render.interior_style_canvas_default_text',
    INTERIOR_STYLE_CANVAS_EDIT: 'render.interior_style_canvas_edit',
    INTERIOR_STYLE_CANVAS_DELETE_CONFIRM: 'render.interior_style_canvas_delete_confirm',
    INTERIOR_STYLE_CANVAS_NAME_LABEL: 'render.interior_style_canvas_name_label',
    INTERIOR_STYLE_CANVAS_NAME_PLACEHOLDER: 'render.interior_style_canvas_name_placeholder',
    INTERIOR_STYLE_CANVAS_RENAME: 'render.interior_style_canvas_rename',
    INTERIOR_STYLE_CANVAS_RENAME_PROMPT: 'render.interior_style_canvas_rename_prompt',
    INTERIOR_STYLE_CANVAS_RENAME_TITLE: 'render.interior_style_canvas_rename_title',
    INTERIOR_STYLE_CANVAS_NAME_MAX_LENGTH: 'render.interior_style_canvas_name_max_length',
    INTERIOR_STYLE_CANVAS_NAME_UPDATE_FAILED: 'render.interior_style_canvas_name_update_failed',
    STYLE_CANVAS_SAVE_FAILED: 'render.style_canvas_save_failed',
    INTERIOR_STYLE_CANVAS_DEFAULT_NAME: 'render.interior_style_canvas_default_name',
    INTERIOR_STYLE_CANVAS_LAYOUT_ALT: 'render.interior_style_canvas_layout_alt',
    FACADE_STYLE_CANVAS_TITLE: 'render.facade_style_canvas_title',
    FACADE_STYLE_CANVAS_CHOOSER_TITLE: 'render.facade_style_canvas_chooser_title',
    FACADE_LAYOUT_TEST1: 'render.facade_layout_test1',
    FACADE_LAYOUT_TEST2: 'render.facade_layout_test2',
    FACADE_LAYOUT_TEST3: 'render.facade_layout_test3',
    INTERIOR_SCENERY_TITLE: 'render.interior_scenery_title',
    INTERIOR_SCENERY_OPTIONAL_HINT: 'render.interior_scenery_optional_hint',
    INTERIOR_STYLE_CHOOSER_TITLE: 'render.interior_style_chooser_title',
    INTERIOR_STYLIZED_TITLE: 'render.interior_stylized_title',
    INTERIOR_STYLIZED_SKETCH: 'render.interior_stylized_sketch',
    INTERIOR_STYLIZED_WATERCOLOR: 'render.interior_stylized_watercolor',
    INTERIOR_STYLIZED_REALISM: 'render.interior_stylized_realism',
  },

  // 材料用量预测 / 使用预测（Usage Prediction）
  USAGE: {
    ENTER_NUMBER_OF_LAYERS: 'usage.enter_number_of_layers',
    ENTER_FLOOR_HEIGHT_M: 'usage.enter_floor_height_m',
    ENTER_BASIC_DESIGN_ACCELERATION: 'usage.enter_basic_design_acceleration',
    ENTER_SITE_CATEGORY: 'usage.enter_site_category',
    ENTER_EARTHQUAKE_GROUPING: 'usage.enter_earthquake_grouping',
  },
};

/**
 * 获取所有翻译key的扁平化数组
 * 用于验证和调试
 */
export function getAllTranslationKeys() {
  const keys = [];

  /**
   * @param {any} obj
   * @param {string} prefix
   */
  function extractKeys(obj, prefix = '') {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        keys.push(value);
      } else if (typeof value === 'object' && value !== null) {
        extractKeys(value, prefix ? `${prefix}.${key}` : key);
      }
    }
  }

  extractKeys(I18N_KEYS);
  return keys;
}

/**
 * 验证翻译key是否存在于常量定义中
 * @param {string} key
 * @returns {boolean}
 */
export function isValidTranslationKey(key) {
  return getAllTranslationKeys().includes(key);
}



// 导出默认对象以便于使用
export default I18N_KEYS;
